import { useEffect, useRef, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { requester, bffRequester } from "App";
import { AuthReducer } from "shared/redux/reducers/auth/AuthReducer";
import { validateInputs, isAdult } from "shared/utils/Utils";
import biometricService from "shared/BiometricService";
import pinVerificationManager from "utils/PinVerificationManager";
import Container from "layouts/container/Container";
import SecureInput from "components/inputs/signUp/SecureInput";
import { mobileNoFormat } from "InitialData/regExp";
import styles from "./AuthenticationFlow.module.css";
import { AuthContext } from "providers/AuthProvider"; // 🔥 AuthContext 추가
import { devLog, devWarn, devError } from "utils/logger";
// 약관 내용 컴포넌트 import
import PolicyPage from "routes/front/mobile/policy/PolicyPage";
import PrivacyPolicy from "routes/front/mobile/policy/PrivacyPolicy";

// 인증 흐름 단계 상수
const STEPS = {
  PHONE_NUMBER: 0,
  AUTHENTICATION_METHOD: 1,  // 🔥 인증 방식 선택 단계
  RESIDENT_NUMBER: 2,
  MOBILE_CARRIER: 3,
  NAME: 4,
  TERMS: 5,
  SMS_VERIFICATION: 6,
  PASS_VERIFICATION: 7,      // 🔥 PASS 인증 단계
  PIN_REGISTRATION: 8,
  BIOMETRIC_OPTION: 9,
  PIN_VERIFICATION: 10       // PIN 검증 단계 조정
};

// 통신사 목록
const CARRIERS = [
  { id: "SKT", name: "SKT" },
  { id: "KT", name: "KT" },
  { id: "LGU", name: "LG U+" },
  { id: "SKTMVNO", name: "SKT 알뜰폰" },
  { id: "KTMVNO", name: "KT 알뜰폰" },
  { id: "LGUMVNO", name: "LG U+ 알뜰폰" }
];

// 커스텀 스타일 객체 추가
const customInputStyle = {
  border: "none",
  borderBottom: "1px solid #dddddd",
  borderRadius: "0",
  padding: "0",
  fontSize: "18px",
  backgroundColor: "transparent",
  color: "#333333",
  height: "56px",
};

// 키보드 컨트롤 함수 추가
const showNativeKeyboard = (inputId) => {
  // 네이티브 브릿지가 있는 경우 키보드 표시 요청
  if (window.KeyboardControl && typeof window.KeyboardControl.showKeyboard === 'function') {
    window.KeyboardControl.showKeyboard(inputId);
  } else if (window.BRIDGE && typeof window.BRIDGE.showKeyboard === 'function') {
    // 직접 브릿지 호출
    window.BRIDGE.showKeyboard(inputId);
  }
};

const hideNativeKeyboard = () => {
  // 네이티브 브릿지가 있는 경우 키보드 숨김 요청
  if (window.KeyboardControl && typeof window.KeyboardControl.hideKeyboard === 'function') {
    window.KeyboardControl.hideKeyboard();
  } else if (window.BRIDGE && typeof window.BRIDGE.hideKeyboard === 'function') {
    // 직접 브릿지 호출
    window.BRIDGE.hideKeyboard();
  }
};

// 웹뷰 키보드 스크롤 관리용 유틸리티
const setupKeyboardScrollHandling = () => {
  // 입력 필드 포커스 시 처리
  const handleInputFocus = (e) => {
    // 포커스된 입력 필드에 대한 정보
    const focusedElement = e.target;

    // 스크롤 조정을 위한 시간차 적용 (키보드가 완전히 올라온 후)
    setTimeout(() => {
      // iOS Safari 등에서 viewport가 조정되는 문제 처리
      window.scrollTo(0, 0);

      // 네이티브 앱에서 실행 중인지 확인
      const isInApp = !!window.AndroidKeyboard || !!window.webkit?.messageHandlers?.keyboard;

      if (isInApp) {
        // 앱 내에서 실행 중인 경우 네이티브에 스크롤 요청
        try {
          if (window.AndroidKeyboard) {
            window.AndroidKeyboard.keyboardOpened();
          } else if (window.webkit?.messageHandlers?.keyboard) {
            window.webkit.messageHandlers.keyboard.postMessage({ action: 'keyboardOpened' });
          }
        } catch (e) {
          devLog('Native keyboard notification error:', e);
        }
      }

      // 요소 위치 계산 및 스크롤
      try {
        // 스크롤 위치 계산
        const rect = focusedElement.getBoundingClientRect();
        const elementBottom = rect.bottom;
        const viewportHeight = window.innerHeight;

        // 키보드 높이 추정 (대략적인 값, 정확한 값은 네이티브에서 제공받는 것이 이상적)
        const estimatedKeyboardHeight = viewportHeight * 0.4; // 화면 높이의 약 40%

        // 입력 필드가 키보드에 가려질 것으로 예상되면 스크롤 조정
        if (elementBottom > viewportHeight - estimatedKeyboardHeight) {
          focusedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      } catch (e) {
        devLog('Scroll adjustment error:', e);
      }
    }, 350); // 키보드가 완전히 올라오는데 걸리는 시간 고려
  };

  // 블러 처리 (키보드가 내려갈 때)
  const handleInputBlur = () => {
    // 필요시 추가 처리
  };

  // 이벤트 리스너 등록
  document.addEventListener('focus', handleInputFocus, true);
  document.addEventListener('blur', handleInputBlur, true);

  // 클린업 함수 반환
  return () => {
    document.removeEventListener('focus', handleInputFocus, true);
    document.removeEventListener('blur', handleInputBlur, true);
  };
};

// 주민번호 앞 6자리를 8자리 생년월일로 변환하는 유틸리티 함수
const getBirthDateFromResidentNumber = (residentNumber, residentNumberBack) => {
  if (residentNumber.length === 6 && residentNumberBack) {
    // 뒷자리 첫 번째 숫자가 1,2면 1900년대, 3,4면 2000년대
    const prefix = parseInt(residentNumberBack.charAt(0)) < 3 ? "19" : "20";
    return prefix + residentNumber;
  }
  return "";
};

function AuthenticationFlow(props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputs = useRef([]);
  const isVerifyingRef = useRef(false);  // isVerifying을 ref로 변경

  // 🔥 AuthContext에서 로그인 상태 확인
  const { userId, userName } = useContext(AuthContext);

  // 이전 전화번호 값을 기억하기 위한 ref 추가
  const prevPhoneNumber = useRef("");

  // 상태 관리
  const [currentStep, setCurrentStep] = useState(STEPS.PHONE_NUMBER);
  const [isLoading, setIsLoading] = useState(false);
  const [isExistingMember, setIsExistingMember] = useState(false);

  // 폼 데이터
  const [formData, setFormData] = useState({
    phoneNumber: "",
    residentNumberFront: "",
    residentNumberBack: "",
    carrierCode: "",
    name: "",
    smsCode: "",
    pinNumber: "",
    pinHash: "", // PIN 해시 값 추가
    termsAgreed: true,
    privacyAgreed: true,
    marketingAgreed: false,
    authenticationMethod: "", // 🔥 인증 방식 (PASS/SMS)
  });

  // 생체인증 상태
  const [biometricStatus, setBiometricStatus] = useState({
    supported: false,
    platform: 'unknown',
    loading: false,
    error: null
  });

  // SMS 인증 관련 데이터를 저장하는 상태 추가
  const [authData, setAuthData] = useState({
    tokenData: null,
    MOKConfirmData: null,
    requestData: null,
    verificationResult: null
  });

  // SMS 인증번호 요청 단계에서 타이머 관련 상태 추가
  const [timeRemaining, setTimeRemaining] = useState(180); // 3분
  const [isTimerActive, setIsTimerActive] = useState(false);

  // 완료된 단계 정보를 저장하는 상태를 변경합니다
  // completedSteps 배열 대신, 각 단계가 완료되었는지 여부를 객체로 관리
  const [completedSteps, setCompletedSteps] = useState({});

  // 상태 변수에 수정 모드 추가
  const [isEditMode, setIsEditMode] = useState(false);

  // 약관 모달 관련 상태 추가
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [modalTitle, setModalTitle] = useState('');

  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const MAX_VERIFICATION_ATTEMPTS = 5;

  // PIN 검증 관련 상태 추가
  const [pinVerificationAttempts, setPinVerificationAttempts] = useState(0);
  const [pinVerificationState, setPinVerificationState] = useState('idle'); // idle, verifying, server_verifying, failed, ready_for_retry
  const MAX_PIN_ATTEMPTS = 3;

  // 🔥 재인증 상태 관리 추가
  const [isReauthentication, setIsReauthentication] = useState(false);

  // 🔥 재인증 완료 상태 추가 (PIN 업데이트 완료 후 생체인증 옵션 제공용)
  const [isReauthenticationComplete, setIsReauthenticationComplete] = useState(false);

  // 🔥 재인증 상태 변경 시 로딩 상태 초기화
  useEffect(() => {
    if (isReauthentication) {
      devLog("[재인증] 재인증 상태로 전환 - 로딩 상태 초기화");
      setIsLoading(false); // 🔥 로딩 상태 초기화
      setPinVerificationState('idle'); // PIN 검증 상태도 초기화
    }
  }, [isReauthentication]);

  // 🔥 PinVerificationManager에서 재인증 상태를 설정할 수 있도록 전역 함수 등록
  useEffect(() => {
    window.setIsReauthentication = setIsReauthentication;

    return () => {
      // 컴포넌트 언마운트 시 정리
      delete window.setIsReauthentication;
    };
  }, []);

  // 컴포넌트 마운트 시 생체인증 지원 여부 확인
  useEffect(() => {
    const isSupported = biometricService.isSupported();
    setBiometricStatus(prev => ({
      ...prev,
      supported: isSupported,
      platform: biometricService.getPlatform()
    }));

    // 🔥 심사용 계정 목록 가져오기 (하이픈 제거)
    const reviewAccountsString = process.env.REACT_APP_REVIEW_ACCOUNTS || '';
    const reviewAccountNumbers = reviewAccountsString.split(',').filter(Boolean).map(num => num.replace(/-/g, ''));

    // 디바이스에 저장된 연락처 확인
    const savedPhoneNumber = biometricService.getPhoneNumber();
    if (savedPhoneNumber) {
      const sanitizedSavedPhoneNumber = savedPhoneNumber.replace(/-/g, '');
      // 🔥 심사용 계정이면 자동 입력 방지
      if (reviewAccountNumbers.includes(sanitizedSavedPhoneNumber)) {
        devLog("[AuthenticationFlow] 심사용 계정 감지 - 전화번호 자동 입력 건너뛰기:", savedPhoneNumber);
      } else {
        setFormData(prev => ({ ...prev, phoneNumber: savedPhoneNumber }));
      }
    }

    // 🔥 네이티브로부터 전화번호 수신 이벤트 리스너 추가
    const handlePhoneNumberReceived = (event) => {
      const { phoneNumber } = event.detail;
      devLog("[AuthenticationFlow] 네이티브로부터 전화번호 수신:", phoneNumber);
      
      if (phoneNumber) {
        const sanitizedReceivedPhoneNumber = phoneNumber.replace(/-/g, '');

        // 🔥 심사용 계정이면 자동 입력 방지
        if (reviewAccountNumbers.includes(sanitizedReceivedPhoneNumber)) {
          devLog("[AuthenticationFlow] 네이티브로부터 수신된 번호가 심사용 계정 - 자동 입력 건너뛰기:", phoneNumber);
        } else {
          setFormData(prev => ({ 
            ...prev, 
            phoneNumber: phoneNumber 
          }));
          devLog("[AuthenticationFlow] 전화번호 상태 업데이트 완료:", phoneNumber);
        }
      }
    };

    // 이벤트 리스너 등록
    window.addEventListener('phoneNumberReceived', handlePhoneNumberReceived);

    // 클린업 함수
    return () => {
      window.removeEventListener('phoneNumberReceived', handlePhoneNumberReceived);
    };
  }, []);

  // 초기 상태 체크 로직 추가
  useEffect(() => {
    const initializeAuth = async () => {
      // 1. 플랫폼 감지
      const platform = biometricService.getPlatform();
      
      // 2. 전화번호 상태 즉시 확인
      const savedPhoneNumber = biometricService.getPhoneNumber();
      
      // 🔥 심사용 계정 목록 가져오기 (하이픈 제거)
      const reviewAccountsString = process.env.REACT_APP_REVIEW_ACCOUNTS || '';
      const reviewAccountNumbers = reviewAccountsString.split(',').filter(Boolean).map(num => num.replace(/-/g, ''));
      
      // 3. 전화번호가 이미 있는 경우 지연 없이 즉시 처리
      if (savedPhoneNumber) {
        const sanitizedSavedPhoneNumber = savedPhoneNumber.replace(/-/g, '');

        // 🔥 심사용 계정이 아니면 자동 입력 진행
        if (!reviewAccountNumbers.includes(sanitizedSavedPhoneNumber)) {
          devLog("[initializeAuth] 저장된 전화번호 확인, 즉시 처리:", savedPhoneNumber);
        
          // 상태 즉시 업데이트
          setFormData(prev => ({
            ...prev,
            phoneNumber: savedPhoneNumber
          }));
        
          // 🔥 주석처리: 모든 사용자가 인증을 거치도록 수정
          // 기존 회원으로 설정하고 PIN 검증 단계로 즉시 이동
          // setIsExistingMember(true);
          // setCurrentStep(STEPS.PIN_VERIFICATION);
        
          devLog("[initializeAuth] 즉시 PIN 검증 단계로 이동 완료");
          return; // 더 이상 진행하지 않음
        } else {
          devLog("[initializeAuth] 심사용 계정 감지 - 전화번호 자동 입력 건너뛰기:", savedPhoneNumber);
        }
      }
      
      // 4. 전화번호가 없는 경우에만 iOS 동기화 지연 적용
      if (platform === 'ios') {
        devLog("[initializeAuth] iOS 플랫폼 감지 - 300ms 동기화 지연 적용 (전화번호 없음)");
        await new Promise(resolve => setTimeout(resolve, 150)); // 🔥 300ms → 150ms로 단축 (속도 개선)
        
        // 지연 후 다시 확인
        const delayedPhoneNumber = biometricService.getPhoneNumber();
        const hasPin = biometricService.hasPin();

        devLog("[initializeAuth] 지연 후 저장된 정보 확인:", {
          platform,
          delayedPhoneNumber,
          hasPin
        });

        // 5. 상태 업데이트
        setFormData(prev => ({
          ...prev,
          phoneNumber: delayedPhoneNumber || ""
        }));

        // 6. 저장된 정보에 따른 단계 결정
        // 🔥 주석처리: 모든 사용자가 인증을 거치도록 수정
        // if (delayedPhoneNumber && hasPin) {
        //   devLog("[initializeAuth] 자동 로그인 가능 - PIN 검증 단계로 이동");
        //   setCurrentStep(STEPS.PIN_VERIFICATION);
        // } else if (delayedPhoneNumber && !hasPin) {
        //   devLog("[initializeAuth] 기존 회원 로그아웃 후 상황 - PIN 검증 단계로 이동");
        //   setIsExistingMember(true);
        //   setCurrentStep(STEPS.PIN_VERIFICATION);
        // } else {
        //   devLog("[initializeAuth] 신규 설치 - 전화번호 입력부터 시작");
        // }
        
        // 🔥 모든 사용자가 전화번호 입력부터 시작하도록 수정
        devLog("[initializeAuth] 모든 사용자 - 전화번호 입력부터 시작");
      } else {
        devLog("[initializeAuth] 안드로이드 플랫폼 - 즉시 처리");
      }
    };

    initializeAuth();
  }, []);

  // 🔥 PASS 인증 실패 횟수 추적
  const [passVerificationAttempts, setPassVerificationAttempts] = useState(0);
  const MAX_PASS_ATTEMPTS = 3;

  // SMS 자동 인식을 위한 핸들러 함수 등록
  useEffect(() => {
    // SMS 코드 수신 핸들러 함수 등록
    window.handleSmsCode = (code) => {
      devLog("[handleSmsCode] SMS 인증번호 자동 수신됨:", code);

      if (code && code.length === 6 && currentStep === STEPS.SMS_VERIFICATION) {
        // formData 상태 업데이트
        setFormData(prev => ({ ...prev, smsCode: code }));

        // 약간의 지연 후 자동 검증 실행
        setTimeout(() => {
          verifySmsCode(code);
        }, 500);
      }
    };

    // ✅ JS가 준비됐음을 Native에 알림
    if (window.BRIDGE?.notifySmsHandlerReady) {
      devLog("[JS → Native] notifySmsHandlerReady() 호출");
      window.BRIDGE.notifySmsHandlerReady();  // 🔥 이 한 줄이 핵심!
    }

    // 컴포넌트 언마운트 시 정리
    return () => {
      delete window.handleSmsCode;
    };
  }, [currentStep]);


  // 저장된 휴대폰 번호가 로드된 후 자동으로 다음 버튼 활성화 및 클릭 처리
  useEffect(() => {
    // 현재 단계가 휴대폰 번호 입력 단계이고, 전화번호가 이미 있고, 수정 모드가 아닐 때만 자동 진행
    if (currentStep === STEPS.PHONE_NUMBER && !isLoading && !isEditMode) {
      const numericValue = formData.phoneNumber.replace(/-/g, '');

      // 11자리 휴대폰 번호가 아니면 자동 진행하지 않음
      if (numericValue.length !== 11) {
        return;
      }

      // 🔥 심사용 계정인 경우, 자동 진행(로그인)을 막습니다.
      const reviewAccountsString = process.env.REACT_APP_REVIEW_ACCOUNTS || '';
      const reviewAccountNumbers = reviewAccountsString.split(',').filter(Boolean).map(num => num.replace(/-/g, ''));
      if (reviewAccountNumbers.includes(numericValue)) {
        devLog("[Auto-Login-Effect] 심사용 계정 감지, 자동 진행을 중단합니다.", numericValue);
        return;
      }

      // 재인증 상태일 때는 더 긴 지연 시간 적용 (DOM 안정화 대기)
      const delayTime = isReauthentication ? 800 : 300;

      // 약간의 지연 후 다음 단계로 자동 진행 (UI가 완전히 렌더링된 후)
      clearTimeout(window.savedPhoneTimeoutId);
      window.savedPhoneTimeoutId = setTimeout(() => {
        const actionButton = document.querySelector(`.${styles.actionButton}`);
        if (actionButton && !actionButton.disabled) {
          // 버튼이 비활성화 상태가 아닌 경우에만 실행
          actionButton.classList.add(styles.buttonPressed);
          setTimeout(() => {
            actionButton.classList.remove(styles.buttonPressed);
            // 🔥 즉시 회원 확인 후 라우팅
            checkExistingMemberAndRoute();
          }, 150);
        }
      }, delayTime);
    }
  }, [formData.phoneNumber, currentStep, isLoading, isEditMode, isReauthentication]);

  // 휴대폰 번호 감지용 useEffect 수정
  useEffect(() => {
    // 수정 버튼을 통한 isEditMode 변경은 자동 진행에서 제외하기 위한 플래그 추가
    const isNumberChanged = formData.phoneNumber !== prevPhoneNumber.current;
    prevPhoneNumber.current = formData.phoneNumber;

    // 현재 단계가 휴대폰 번호 입력 단계이고, 수정 모드이고, 번호가 실제로 변경되었을 때만 감지
    if (currentStep === STEPS.PHONE_NUMBER && isEditMode && !isLoading && isNumberChanged) {
      // 하이픈이 제거된 순수 번호가 11자리인 경우
      const numericValue = formData.phoneNumber.replace(/-/g, '');

      if (numericValue.length === 11) {
        // 사용자가 충분히 확인할 수 있는 지연 시간 후 다음 단계로 진행
        clearTimeout(window.phoneInputCompletedTimeoutId);
        window.phoneInputCompletedTimeoutId = setTimeout(() => {
          // 먼저 스택 정보 업데이트
          const phoneNumberInfo = {
            id: STEPS.PHONE_NUMBER,
            title: t("휴대폰 번호"),
            value: formData.phoneNumber
          };

          setCompletedSteps(prev => ({
            ...prev,
            [STEPS.PHONE_NUMBER]: phoneNumberInfo
          }));

          // 버튼 효과 처리
          setTimeout(() => {
            const actionButton = document.querySelector(`.${styles.actionButton}`);
            if (actionButton && !actionButton.disabled) {
              actionButton.classList.add(styles.buttonPressed);
              setTimeout(() => {
                actionButton.classList.remove(styles.buttonPressed);
                // 🔥 즉시 회원 확인 후 라우팅
                checkExistingMemberAndRoute();
              }, 150);
                          } else {
                // 버튼 없는 경우 직접 진행
                checkExistingMemberAndRoute();
              }
          }, 100);
        }, 300); // 사용자가 확인할 충분한 시간
      }
    }
  }, [formData.phoneNumber, currentStep, isEditMode, isLoading]);

  // 타이머 관련 코드 추가
  useEffect(() => {
    let timer;
    if (isTimerActive && timeRemaining > 0) {
      timer = setInterval(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
    } else if (timeRemaining === 0) {
      setIsTimerActive(false);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isTimerActive, timeRemaining]);

  // 타이머 형식 변환 함수
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 키보드 스크롤 처리 설정
  useEffect(() => {
    // 키보드 스크롤 핸들링 설정
    const cleanup = setupKeyboardScrollHandling();

    // 컴포넌트 언마운트 시 클린업
    return cleanup;
  }, []);

  // 각 단계로 이동 시 키보드 자동 표시 로직 개선
  useEffect(() => {
    // 포커스를 설정해야 하는 단계에서만 키보드 표시
    if ([STEPS.PHONE_NUMBER, STEPS.RESIDENT_NUMBER, STEPS.NAME, STEPS.SMS_VERIFICATION].includes(currentStep)) {
      // 약간의 지연 후 키보드 표시 (렌더링 완료 후)
      setTimeout(() => {
        // 🔥 단계별 올바른 input 인덱스 매핑
        let inputIndex;
        switch (currentStep) {
          case STEPS.PHONE_NUMBER:
            inputIndex = 0;
            break;
          case STEPS.RESIDENT_NUMBER:
            inputIndex = 1; // 주민번호 앞자리
            break;
          case STEPS.NAME:
            inputIndex = 3;
            break;
          case STEPS.SMS_VERIFICATION:
            inputIndex = 4;
            break;
          default:
            inputIndex = 0;
        }

        const focusableInput = inputs.current[inputIndex];
        if (focusableInput) {
          focusableInput.focus();

          // 입력 필드가 화면 하단에 있을 경우 스크롤 조정
          const inputElement = document.getElementById(focusableInput.getId ? focusableInput.getId() : '');
          if (inputElement) {
            try {
              // 요소 위치 계산
              const rect = inputElement.getBoundingClientRect();
              const elementBottom = rect.bottom;
              const viewportHeight = window.innerHeight;

              // 키보드 높이 추정
              const estimatedKeyboardHeight = viewportHeight * 0.4;

              // 스크롤 조정 필요 여부 확인
              if (elementBottom > viewportHeight - estimatedKeyboardHeight) {
                // DOM 요소가 여전히 존재하는지 확인 후 scrollIntoView 실행
                if (document.body.contains(inputElement)) {
                  inputElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                  });
                }
              }
            } catch (scrollError) {
              devWarn('ScrollIntoView error (무시 가능):', scrollError);
            }
          }

          showNativeKeyboard(focusableInput.getId ? focusableInput.getId() : '');
        }
      }, 300);
    } else if (currentStep === STEPS.MOBILE_CARRIER) {
      // 통신사 선택 단계에서 자동으로 select 열기
      setTimeout(() => {
        try {
          // 네이티브 브릿지를 통한 시도
          if (window.BRIDGE && typeof window.BRIDGE.openSelectPicker === 'function') {
            window.BRIDGE.openSelectPicker('carrierSelect');
          } else {
            // 네이티브 브릿지가 없거나 메서드가 없는 경우 직접 시도
            const carrierSelect = document.getElementById('carrierSelect');
            if (carrierSelect && typeof carrierSelect.focus === 'function') {
              try {
                carrierSelect.focus();

                // 일부 브라우저에서는 focus 이후 스크롤이 필요할 수 있음
                const selectRect = carrierSelect.getBoundingClientRect();
                const viewportHeight = window.innerHeight;

                // 화면 중앙에 위치하도록 스크롤 조정
                if (selectRect && selectRect.bottom > viewportHeight * 0.75) {
                  // DOM 요소가 여전히 존재하는지 확인 후 scrollIntoView 실행
                  if (document.body.contains(carrierSelect)) {
                    carrierSelect.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center'
                    });
                  }
                }
              } catch (focusError) {
                devError('Select focus error:', focusError);
              }
            }
          }
        } catch (e) {
          devError('Select handling error:', e);
        }
      }, 400);
    }
  }, [currentStep]);

  // 핸들러 함수들 - 전화번호 처리 로직 단순화
  const handleInputChange = (field, value) => {
    // 이전 상태 값 기억
    const prevValue = formData[field];

    // 상태 업데이트
    setFormData(prev => ({ ...prev, [field]: value }));

    // 수정 모드로 변경 (값이 변경되면 수정 중인 것으로 간주)
    setIsEditMode(true);

    // 주민등록번호 앞자리 6자리 입력 시 자동으로 뒷자리 입력 필드로 포커스 이동
    if (field === 'residentNumberFront' && value.length === 6 && /^\d{6}$/.test(value)) {
      try {
        const nextInput = inputs.current[2];
        if (nextInput && typeof nextInput.focus === 'function') {
          setTimeout(() => {
            try {
              nextInput.focus();
              // 네이티브 키보드가 사용 가능한 경우 키보드 표시
              const inputId = nextInput.getId ? nextInput.getId() : '';
              if (inputId) {
                showNativeKeyboard(inputId);
              }
            } catch (focusError) {
              devError('Focus error:', focusError);
            }
          }, 100);
        }
      } catch (error) {
        devError('Input focus error:', error);
      }
    }

    // 주민등록번호 뒷 첫번째 자리 입력 시 자동으로 다음 단계로 이동
    // 수정: 이전 값보다 길어질 때만 자동 진행, 값을 지우는 경우 자동 진행하지 않음
    if (field === 'residentNumberBack') {
      // 이전 값의 길이와 비교 (지우는 경우 자동 진행 안함)
      const prevLength = prevValue ? prevValue.length : 0;
      const isInputGrowing = value.length > prevLength;

      // 자동 진행 조건: 1자리 이상 입력됨 + 앞자리 6자리 입력됨 + 입력값이 증가함 + 수정 모드가 일반모드(또는 새로운 입력)
      if (value.length >= 1 && formData.residentNumberFront.length === 6 && isInputGrowing) {
        clearTimeout(window.residentNumberTimeoutId); // 이전 타이머 취소
        window.residentNumberTimeoutId = setTimeout(() => {
          // 버튼 클릭 효과 추가
          const actionButton = document.querySelector(`.${styles.actionButton}`);
          if (actionButton) {
            actionButton.classList.add(styles.buttonPressed);
            setTimeout(() => {
              actionButton.classList.remove(styles.buttonPressed);
              // 자동 진행
              moveToNextStep(STEPS.MOBILE_CARRIER);
            }, 150);
          } else {
            // 버튼 없으면 바로 진행
            moveToNextStep(STEPS.MOBILE_CARRIER);
          }
        }, 300);
      }
    }

    // 🔥 인증 방식 선택 시 자동 진행은 useEffect에서 처리 (라이프사이클 안정성)

    // 통신사 코드 선택 시 자동으로 다음 단계로 이동
    if (field === 'carrierCode' && value) {
      // 통신사 정보를 즉시 완료된 단계로 표시
      const carrierInfo = {
        id: STEPS.MOBILE_CARRIER,
        title: t("통신사"),
        value: CARRIERS.find(c => c.id === value)?.name || value
      };

      // 즉시 완료된 단계 정보 업데이트
      setCompletedSteps(prev => ({
        ...prev,
        [STEPS.MOBILE_CARRIER]: carrierInfo
      }));

      // 약간의 지연을 두어 사용자가 선택한 통신사를 볼 수 있게 함
      clearTimeout(window.carrierTimeoutId);
      window.carrierTimeoutId = setTimeout(() => {
        // 버튼 클릭 효과 추가
        const actionButton = document.querySelector(`.${styles.actionButton}`);
        if (actionButton) {
          actionButton.classList.add(styles.buttonPressed);
          setTimeout(() => {
            actionButton.classList.remove(styles.buttonPressed);
            // 🔥 인증 방식에 따른 자동 진행 분기
            if (formData.authenticationMethod === 'PASS') {
              setCurrentStep(STEPS.NAME); // PASS는 바로 이름으로
            } else if (formData.authenticationMethod === 'SMS') {
              setCurrentStep(STEPS.NAME); // SMS도 이름 입력으로 (주민번호는 이미 입력 완료)
            } else {
              setCurrentStep(STEPS.NAME); // 기본값
            }
            setIsEditMode(false);
          }, 150);
        } else {
          // 버튼 없으면 바로 진행
          if (formData.authenticationMethod === 'PASS') {
            setCurrentStep(STEPS.NAME);
          } else if (formData.authenticationMethod === 'SMS') {
            setCurrentStep(STEPS.NAME); // SMS도 이름 입력으로 (주민번호는 이미 입력 완료)
          } else {
            setCurrentStep(STEPS.NAME);
          }
          setIsEditMode(false);
        }
      }, 400);
    }

    // SMS 인증번호 6자리 입력 시 자동으로 다음 단계로 이동
    if (field === 'smsCode' && value.length === 6) {
      // 즉시 상태 업데이트 (이미 위에서 처리됨)
      devLog("[handleInputChange] SMS 인증번호 6자리 입력 완료:", value);

      clearTimeout(window.smsTimeoutId); // 이전 타이머 취소
      window.smsTimeoutId = setTimeout(() => {
        // 로딩 중이 아닐 때만 진행
        if (!isLoading) {
          // 버튼 클릭 효과 추가
          const actionButton = document.querySelector(`.${styles.actionButton}`);
          if (actionButton) {
            actionButton.classList.add(styles.buttonPressed);
            setTimeout(() => {
              actionButton.classList.remove(styles.buttonPressed);
              // 자동 진행 - 최신 값을 직접 전달
              verifySmsCode(value);
            }, 150);
          } else {
            // 버튼 없으면 바로 진행 - 최신 값을 직접 전달
            verifySmsCode(value);
          }
        }
      }, 500); // 타이머를 약간 늘려 상태 업데이트가 완료될 시간 확보
    }
  };

  const handleCheckboxChange = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // 단계 이동 시 완료된 단계 정보 업데이트 - 로직 개선
  const moveToNextStep = (nextStep) => {
    // 키보드 숨기기
    hideNativeKeyboard();

    // 현재 단계의 정보 가져오기
    const currentStepInfo = getStepInfo(currentStep);

    // 현재 단계를 완료된 것으로 표시 (유효한 정보가 있는 경우만)
    if (currentStep < nextStep && currentStepInfo) {
      // 즉시 완료된 단계 정보 업데이트 (지연 제거)
      setCompletedSteps(prev => ({
        ...prev,
        [currentStep]: currentStepInfo
      }));

      // 수정 모드 초기화 (다음 단계로 이동했으므로 이제 일반 모드로 전환)
      setIsEditMode(false);
    }

    // 다음 단계로 이동
    setCurrentStep(nextStep);
  };

  // 각 단계별 정보 반환 함수 - 항상 최신 상태 반영하도록 개선
  const getStepInfo = (step) => {
    switch (step) {
      case STEPS.PHONE_NUMBER:
        return {
          id: STEPS.PHONE_NUMBER,
          title: t("휴대폰 번호"),
          value: formData.phoneNumber // 항상 현재 상태의 번호 사용
        };
      case STEPS.RESIDENT_NUMBER:
        return {
          id: STEPS.RESIDENT_NUMBER,
          title: t("주민등록번호"),
          value: `${formData.residentNumberFront}-*******`
        };
      case STEPS.MOBILE_CARRIER:
        return {
          id: STEPS.MOBILE_CARRIER,
          title: t("통신사"),
          value: CARRIERS.find(c => c.id === formData.carrierCode)?.name || formData.carrierCode
        };
      case STEPS.NAME:
        return {
          id: STEPS.NAME,
          title: t("이름"),
          value: formData.name
        };
      default:
        return null;
    }
  };

  // 전화번호로 진행하는 새로운 함수 추가 (현재 입력값을 파라미터로 직접 전달받음)
  const proceedWithPhoneNumber = (phoneNumber) => {
    devLog("==============================================================");
    devLog(`[사용자 유형 확인] 시작: 전화번호 ${phoneNumber}`);
    devLog(`[사용자 유형 확인] 재인증 상태: ${isReauthentication}`);

    // 🔥 재인증 상태인 경우 - PIN을 모르므로 본인인증 과정 진행
    if (isReauthentication) {
      devLog("[사용자 유형 확인] 🔥 재인증 상태 - PIN을 모르므로 본인인증 과정 진행");
      devLog("[사용자 유형 확인] 이미 연락처가 저장되어 있으므로 2단계(주민번호)부터 시작");
      devLog("[사용자 유형 확인] 재인증 플래그는 PIN 업데이트 완료 후 초기화");
      // 🔥 재인증 플래그 초기화를 제거 (PIN 업데이트 완료 후에 초기화)
      // setIsReauthentication(false); // 재인증 플래그 초기화
      // 🔥 기존 회원 상태 유지 (false → true로 변경)
      setIsExistingMember(true); // 기존 회원임을 명시 (재인증이므로)
      moveToNextStep(STEPS.RESIDENT_NUMBER); // 2단계(주민번호)부터 시작
      devLog("[사용자 유형 확인] 주민번호 입력 단계로 이동 완료");
      devLog("==============================================================");
      return;
    }

    devLog("[사용자 유형 확인] 로딩 상태 설정: true");
    setIsLoading(true);

    try {
      // API 요청 객체 로깅
      const requestData = { mobileNumber: phoneNumber, storeId: "root", role: "MEMBER" };
      devLog("[사용자 유형 확인] API 요청 데이터:", requestData);
      devLog("[사용자 유형 확인] 기존 회원 확인 API 호출 시작");

      // 기존 회원 확인
      requester.membermobileNumDuplicate(requestData, (result) => {
        devLog("[사용자 유형 확인] API 응답 수신:", result);

        if (result.code === 0) {
          const isDuplicate = result.data;
          devLog(`[사용자 유형 확인] 회원 여부: ${isDuplicate ? '기존 회원' : '신규 회원'}`);

          setIsExistingMember(isDuplicate);
          devLog("[사용자 유형 확인] isExistingMember 상태 업데이트:", isDuplicate);

          // 🔥 주석처리: 모든 사용자가 인증을 거치도록 수정
          // if (isDuplicate) {
          //   // 기존 회원이면 PIN 검증 단계로
          //   devLog("[사용자 유형 확인] 기존 회원 → PIN 검증 단계로 이동");
          //   devLog("[사용자 유형 확인] 현재 단계:", currentStep, "→ 이동할 단계:", STEPS.PIN_VERIFICATION);
          //   setCurrentStep(STEPS.PIN_VERIFICATION);
          // } else {
          //   // 신규 회원이면 본인인증 단계로
          //   devLog("[사용자 유형 확인] 신규 회원 → 본인인증 단계로 이동");
          //   devLog("[사용자 유형 확인] 현재 단계:", currentStep, "→ 이동할 단계:", STEPS.RESIDENT_NUMBER);
          //   moveToNextStep(STEPS.RESIDENT_NUMBER);
          // }
          
          // 🔥 모든 사용자가 인증방식 선택 단계로 이동하도록 수정
          setIsExistingMember(isDuplicate); // 회원 여부는 저장하되 단계는 동일하게 처리
          devLog(`[사용자 유형 확인] ${isDuplicate ? '기존' : '신규'} 회원 → 인증방식 선택으로 이동`);
          moveToNextStep(STEPS.AUTHENTICATION_METHOD);
        } else {
          devError("[사용자 유형 확인] API 오류:", result);
          toast.error(t("회원 확인 중 오류가 발생했습니다."));
        }

        devLog("[사용자 유형 확인] 로딩 상태 설정: false");
        setIsLoading(false);
        devLog("[사용자 유형 확인] 완료");
        devLog("==============================================================");
      });
    } catch (error) {
      devError("[사용자 유형 확인] 예외 발생:", error);
      toast.error(t("서버 연결 중 오류가 발생했습니다."));
      setIsLoading(false);
      devLog("==============================================================");
    }
  };

  // 🔥 전화번호 입력 후 즉시 회원 확인 및 라우팅
  const checkExistingMemberAndRoute = () => {
    devLog("==============================================================");
    devLog(`[checkExistingMemberAndRoute] 시작: 전화번호 ${formData.phoneNumber}`);
    devLog(`[checkExistingMemberAndRoute] 재인증 상태: ${isReauthentication}`);

    
    // 🔥 심사용 계정 백도어: 환경 변수에서 계정 목록을 읽어 바로 로그인
    const sanitizedPhoneNumber = formData.phoneNumber.replace(/-/g, '');
    const reviewAccountsString = process.env.REACT_APP_REVIEW_ACCOUNTS || '';
    const reviewAccountNumbers = reviewAccountsString.split(',').filter(Boolean).map(num => num.replace(/-/g, '')); // 🔥 하이픈 제거 로직 추가

    // 환경 변수에 심사용 계정이 설정된 경우에만 백도어 로직 실행
    if (reviewAccountNumbers.length > 0 && reviewAccountNumbers.includes(sanitizedPhoneNumber)) {
      devLog(`[checkExistingMemberAndRoute] 🚀 심사용 계정 감지 (${sanitizedPhoneNumber}) - 바로 로그인 진행`);
      
      const signInData = {
        storeId: "root",
        userName: sanitizedPhoneNumber, // 하이픈 제거된 번호 사용
        role: "MEMBER"
      };

      devLog("[checkExistingMemberAndRoute] 백도어 로그인 요청:", signInData);
      setIsLoading(true);

      requester.userNumberSignIn(signInData, (signInResult) => {
        devLog("[checkExistingMemberAndRoute] 백도어 로그인 응답:", signInResult);
        setIsLoading(false);

        if (signInResult.code === 0) {
          const tokenData = signInResult.data.token;
          dispatch(AuthReducer.actions.setToken(tokenData));

          // 🔥 백도어 로그인 시에도 네이티브에 연락처 저장
          const phoneNumberSaved = biometricService.savePhoneNumber(sanitizedPhoneNumber);
          devLog(`[checkExistingMemberAndRoute] 백도어용 네이티브 연락처 저장 결과: ${phoneNumberSaved}`);
          
          // 네이티브에 토큰 전달
          if (window.webkit?.messageHandlers?.BRIDGE) {
            window.webkit.messageHandlers.BRIDGE.postMessage({
              command: "setToken",
              token: tokenData
            });
          } else if (window.BRIDGE?.setToken) {
            window.BRIDGE.setToken(tokenData);
          }

          devLog("[checkExistingMemberAndRoute] 백도어 로그인 성공 - 메인으로 이동");
          

          // 메인 페이지로 이동
          setTimeout(() => {
            navigate(`/ws/${props.storeCode}/mobile/main`, { replace: true });
          }, 1000);
        } else {
          devError("[checkExistingMemberAndRoute] 백도어 로그인 실패:", signInResult);
          toast.error("심사용 계정 로그인에 실패했습니다.");
        }
      });

      devLog("==============================================================");
      return;
    }

    // 🔥 재인증 상태인 경우 - PIN을 모르므로 본인인증 과정 진행
    if (isReauthentication) {
      devLog("[checkExistingMemberAndRoute] 🔥 재인증 상태 - PIN을 모르므로 본인인증 과정 진행");
      devLog("[checkExistingMemberAndRoute] 이미 연락처가 저장되어 있으므로 인증방식 선택으로 이동");
      setIsExistingMember(true); // 기존 회원임을 명시 (재인증이므로)
      moveToNextStep(STEPS.AUTHENTICATION_METHOD); // 인증방식 선택으로 이동
      devLog("[checkExistingMemberAndRoute] 인증방식 선택 단계로 이동 완료");
      devLog("==============================================================");
      return;
    }

    devLog("[checkExistingMemberAndRoute] 일반 로그인 - 회원 확인 진행");
    setIsLoading(true);

    try {
      const requestData = { mobileNumber: formData.phoneNumber, storeId: "root", role: "MEMBER" };
      devLog("[checkExistingMemberAndRoute] API 요청 데이터:", requestData);

      requester.membermobileNumDuplicate(requestData, (result) => {
        devLog("[checkExistingMemberAndRoute] API 응답:", result);

        if (result.code === 0) {
          const isDuplicate = result.data;
          devLog(`[checkExistingMemberAndRoute] 회원 여부: ${isDuplicate ? '기존 회원' : '신규 회원'}`);
          setIsExistingMember(isDuplicate);

          // 🔥 주석처리: 모든 사용자가 인증을 거치도록 수정
          // if (isDuplicate) {
          //   // 🔥 기존 회원이면 바로 PIN 검증 단계로 (인증방식 선택 생략)
          //   devLog("[checkExistingMemberAndRoute] 기존 회원 → PIN 검증 단계로 이동");
          //   setCurrentStep(STEPS.PIN_VERIFICATION);
          // } else {
          //   // 🔥 신규 회원이면 인증방식 선택 단계로
          //   devLog("[checkExistingMemberAndRoute] 신규 회원 → 인증방식 선택으로 이동");
          //   moveToNextStep(STEPS.AUTHENTICATION_METHOD);
          // }
          
          // 🔥 모든 사용자가 인증방식 선택 단계로 이동하도록 수정
          setIsExistingMember(isDuplicate); // 회원 여부는 저장하되 단계는 동일하게 처리
          devLog(`[checkExistingMemberAndRoute] ${isDuplicate ? '기존' : '신규'} 회원 → 인증방식 선택으로 이동`);
            moveToNextStep(STEPS.AUTHENTICATION_METHOD);
        } else {
          devError("[checkExistingMemberAndRoute] API 오류:", result);
          toast.error(t("회원 확인 중 오류가 발생했습니다."));
        }

        setIsLoading(false);
        devLog("[checkExistingMemberAndRoute] 완료");
        devLog("==============================================================");
      });
    } catch (error) {
      devError("[checkExistingMemberAndRoute] 예외 발생:", error);
      toast.error(t("서버 연결 중 오류가 발생했습니다."));
      setIsLoading(false);
      devLog("==============================================================");
    }
  };

  // 🔥 인증방식 선택 후 회원 확인 및 라우팅 (신규 회원용)
  const checkExistingMemberAndProceed = (authMethod) => {
    devLog(`[checkExistingMemberAndProceed] ${authMethod} 방식으로 회원 확인 시작`);
    setIsLoading(true);

    try {
      const requestData = { mobileNumber: formData.phoneNumber, storeId: "root", role: "MEMBER" };
      devLog("[checkExistingMemberAndProceed] API 요청 데이터:", requestData);

      requester.membermobileNumDuplicate(requestData, (result) => {
        devLog("[checkExistingMemberAndProceed] API 응답:", result);

        if (result.code === 0) {
          const isDuplicate = result.data;
          devLog(`[checkExistingMemberAndProceed] 회원 여부: ${isDuplicate ? '기존 회원' : '신규 회원'}`);
          setIsExistingMember(isDuplicate);

          // 🔥 주석처리: 모든 사용자가 인증을 거치도록 수정
          // if (isDuplicate) {
          //   // 기존 회원이면 PIN 검증 단계로
          //   devLog("[checkExistingMemberAndProceed] 기존 회원 → PIN 검증 단계로 이동");
          //   setCurrentStep(STEPS.PIN_VERIFICATION);
          // } else {
          //   // 신규 회원이면 인증 방식에 따라 분기
          //   if (authMethod === 'PASS') {
          //     devLog("[checkExistingMemberAndProceed] 신규 회원 + PASS → 이름 입력으로 이동");
          //     moveToNextStep(STEPS.NAME);
          //   } else if (authMethod === 'SMS') {
          //     devLog("[checkExistingMemberAndProceed] 신규 회원 + SMS → 주민번호 입력으로 이동");
          //     moveToNextStep(STEPS.RESIDENT_NUMBER);
          //   }
          // }
          
          // 🔥 모든 사용자가 인증 방식에 따라 동일하게 처리되도록 수정
          setIsExistingMember(isDuplicate); // 회원 여부는 저장하되 단계는 동일하게 처리
            if (authMethod === 'PASS') {
            devLog(`[checkExistingMemberAndProceed] ${isDuplicate ? '기존' : '신규'} 회원 + PASS → 이름 입력으로 이동`);
              moveToNextStep(STEPS.NAME);
            } else if (authMethod === 'SMS') {
            devLog(`[checkExistingMemberAndProceed] ${isDuplicate ? '기존' : '신규'} 회원 + SMS → 주민번호 입력으로 이동`);
              moveToNextStep(STEPS.RESIDENT_NUMBER);
          }
        } else {
          devError("[checkExistingMemberAndProceed] API 오류:", result);
          toast.error(t("회원 확인 중 오류가 발생했습니다."));
        }

        setIsLoading(false);
      });
    } catch (error) {
      devError("[checkExistingMemberAndProceed] 예외 발생:", error);
      toast.error(t("서버 연결 중 오류가 발생했습니다."));
      setIsLoading(false);
    }
  };

  // 기존 checkExistingMember 함수는 호환성을 위해 유지
  const checkExistingMember = () => {
    // 기본값으로 SMS 방식 사용
    checkExistingMemberAndProceed('SMS');
  };

  // 임시 PIN 확인 및 로그인 처리 (추후 네이티브 연동으로 변경)
  const verifyPinAndLogin = () => {
    // 실제 구현에서는 네이티브 브릿지를 통해 PIN 확인 후 로그인
    let signInData = {
      storeId: "root",
      userName: formData.phoneNumber,
      role: "MEMBER"
    };

    requester.userNumberSignIn(signInData, (signInResult) => {
      if (signInResult.code === 0) {
        const tokenData = signInResult.data.token;
        dispatch(AuthReducer.actions.setToken(tokenData));

        // 네이티브에 토큰 전달
        if (window.webkit?.messageHandlers?.BRIDGE) {
          window.webkit.messageHandlers.BRIDGE.postMessage({
            command: "setToken",
            token: tokenData
          });
        }

        navigate(`/ws/${props.storeCode}/mobile/main`, { replace: true });
      } else {
        toast.error(t("로그인 중 오류가 발생했습니다."));
      }
    });
  };

  // SMS 인증번호 재전송
  const resendSmsCode = () => {
    devLog("[resendSmsCode] 시작", {
      authData: {
        hasTokenData: !!authData.tokenData,
        hasMOKConfirmData: !!authData.MOKConfirmData,
        hasRequestData: !!authData.requestData
      }
    });

    setIsLoading(true);

    // API 요청을 위한 토큰 재요청
    devLog("[resendSmsCode] 토큰 재요청 시작");
    bffRequester.getToken((tokenResult) => {
      devLog("[resendSmsCode] 토큰 응답 받음", { tokenResult });

      let tokenData = tokenResult;
      let token = {
        "encryptMOKToken": tokenData.encryptMOKToken,
        "publicKey": tokenData.publicKey
      };
      let jsonData = JSON.stringify(token);

      devLog("[resendSmsCode] 토큰 데이터 준비", { token });

      // 기존 요청 데이터 복제 후 토큰 업데이트
      let data = { ...authData.requestData };

      // 기존 데이터가 없는 경우 (또는 일부 데이터가 누락된 경우) 새로 구성
      if (!data || !data.userBirthday || !data.userGender) {
        devLog("[resendSmsCode] 기존 요청 데이터 불완전, 새로 구성");

        // 생년월일 및 성별 추출 - 컴포넌트 외부 함수 사용
        const birthDay = getBirthDateFromResidentNumber(formData.residentNumberFront, formData.residentNumberBack);
        const gender = formData.residentNumberBack ? (parseInt(formData.residentNumberBack.charAt(0)) % 2 === 1 ? "1" : "2") : "";
        const nation = formData.residentNumberBack ? "0" : "";

        // 앱 서명 해시 가져오기 (네이티브에서 제공하는 경우)
        let appSignatureHash = '';
        if (window.BRIDGE && typeof window.BRIDGE.getAppSignatureHash === 'function') {
          try {
            devLog("[resendSmsCode] 앱 서명 해시 함수 호출 시도");
            appSignatureHash = window.BRIDGE.getAppSignatureHash();
            devLog("[resendSmsCode] 앱 서명 해시:", appSignatureHash);
          } catch (error) {
            devError("[resendSmsCode] 앱 서명 해시 가져오기 실패:", error);
          }
        } else {
          devWarn("[resendSmsCode] getAppSignatureHash 함수를 사용할 수 없음. window.BRIDGE 상태:", !!window.BRIDGE);
        }

        // SMS 메시지 형식 설정 (토스 패턴 참고)
        let smsMessage = appSignatureHash
          ? `푸푸 인증번호 [000000]를 입력해주세요`
          : "푸푸 인증번호 [000000]를 입력해주세요";

        devLog("[resendSmsCode] 해시가 포함된 SMS 메시지:", smsMessage);

        // 필요한 정보 업데이트
        data = {
          "serviceType": "telcoAuth",
          "providerid": formData.carrierCode,
          "reqAuthType": "SMS",
          "usageCode": "01005",
          "userName": formData.name,
          "userPhoneNum": formData.phoneNumber.replace(/-/g, ''),
          "userBirthday": birthDay,
          "userGender": gender,
          "sendMsg": smsMessage,
          "replyNumber": "01048947486",
          "retTransferType": "MOKResult",
          "userNation": nation,
          "normalFlag": "Y",
          ...data
        };

        devLog("[resendSmsCode] 새로 구성된 요청 데이터", {
          birthDay, gender, nation
        });
      }

      // 토큰 데이터 업데이트
      data.MOKAuthRequestData = jsonData;

      devLog("[resendSmsCode] 재요청 데이터 준비", {
        requestData: data
      });

      // SMS 인증 재요청 API 호출
      devLog("[resendSmsCode] API 재요청 시작");
      bffRequester.apiRequest(data, (result) => {
        devLog("[resendSmsCode] API 응답 받음", { result });
        setIsLoading(false);

        // 응답 파싱
        try {
          devLog("[resendSmsCode] 응답 파싱 시작");
          const outerMOKConfirmData = result.MOKConfirmData;
          devLog("[resendSmsCode] outerMOKConfirmData", { outerMOKConfirmData });

          const innerMOKConfirmData = JSON.parse(outerMOKConfirmData);
          devLog("[resendSmsCode] innerMOKConfirmData 파싱 완료", {
            resultCode: innerMOKConfirmData.resultCode,
            resultMsg: innerMOKConfirmData.resultMsg
          });

          if (innerMOKConfirmData.resultCode === "2000") {
            // 인증번호 재요청 성공
            devLog("[resendSmsCode] 인증번호 재요청 성공");

            // 응답 데이터 세션 업데이트
            setAuthData({
              tokenData,
              MOKConfirmData: innerMOKConfirmData,
              requestData: data
            });
            devLog("[resendSmsCode] 인증 데이터 업데이트 완료");

            // 타이머 재설정 및 활성화
            setTimeRemaining(180);
            setIsTimerActive(true);
            devLog("[resendSmsCode] 타이머 재설정 완료", { timeRemaining: 180 });

            // 안드로이드 SMS 인증번호 자동 수신 재시도
            if (biometricStatus.platform === 'android' && window.BRIDGE?.requestSmsRetrieval) {
              devLog("[resendSmsCode] 안드로이드 SMS 자동 수신 재시도");
              try {
                window.BRIDGE.requestSmsRetrieval();
              } catch (error) {
                devError("[resendSmsCode] SMS 자동 수신 재요청 오류:", error);
              }
            }

            toast.info(t("인증번호가 재발송되었습니다"));
          } else {
            // 인증번호 재요청 실패
            devError("[resendSmsCode] 인증번호 재요청 실패", {
              resultCode: innerMOKConfirmData.resultCode,
              resultMsg: innerMOKConfirmData.resultMsg
            });
            toast.error(innerMOKConfirmData.resultMsg || t("인증번호 재요청에 실패했습니다."));
          }
        } catch (error) {
          devError("[resendSmsCode] SMS 인증 재요청 파싱 오류:", error);
          devError("[resendSmsCode] 원본 응답:", result);
          toast.error(t("인증번호 재요청 중 오류가 발생했습니다. 다시 시도해주세요."));
        }
      });
    });

    devLog("[resendSmsCode] 함수 실행 완료 (비동기 작업 진행 중)");
  };

  // 🔥 PASS 인증 확인 (NumberVerificationModal의 passOnClick 로직 참조)
  const verifyPassAuthentication = () => {
    devLog("[verifyPassAuthentication] PASS 인증 확인 시작");
    setIsLoading(true);

    // NumberVerificationModal의 validate 함수 로직 적용
    let validateData = {
      MOKConfirmData: authData.MOKConfirmData,
      session: authData.tokenData.session,
      authNumber: '', // PASS는 인증번호 불필요
      type: "PASS",
      info: {
        userPhoneNum: formData.phoneNumber.replace(/-/g, ''),
        userName: formData.name
      }
    };

    devLog("[verifyPassAuthentication] PASS 검증 요청 데이터", { validateData });

    bffRequester.apiResult(validateData, (result) => {
      devLog("[verifyPassAuthentication] PASS 검증 응답", { result });
      setIsLoading(false);

      if (result.resultData) {
        // PASS 인증 성공
        devLog("[verifyPassAuthentication] PASS 인증 성공");
        
        // 성인 여부 확인
        const isAdultUser = isAdult(result.resultData.userBirthday);
        devLog("[verifyPassAuthentication] 성인 여부 확인", { isAdultUser, birthday: result.resultData.userBirthday });

        if (isAdultUser) {
          // 인증 결과 데이터 저장
          setAuthData(prev => ({
            ...prev,
            verificationResult: result.resultData
          }));

          toast.success(t("PASS 본인인증이 완료되었습니다."));
          
          // 🔥 PASS 인증 성공 시 시도 횟수 초기화
          setPassVerificationAttempts(0);
          
          // 🔥 PASS 인증 완료 후 무조건 PIN 등록 단계로 이동 (SMS와 동일)
          devLog("[verifyPassAuthentication] PASS 인증 성공 - PIN 등록 단계로 이동");
          moveToNextStep(STEPS.PIN_REGISTRATION);
        } else {
          devWarn("[verifyPassAuthentication] 성인이 아님");
          toast.error(t("성인만 가입할 수 있습니다."));
        }
      } else {
        // 🔥 PASS 인증 실패 처리
        devError("[verifyPassAuthentication] PASS 인증 실패");
        
        const newAttempts = passVerificationAttempts + 1;
        setPassVerificationAttempts(newAttempts);
        
        if (newAttempts >= MAX_PASS_ATTEMPTS) {
          // 3회 실패 시 재인증 요청
          devLog("[verifyPassAuthentication] PASS 인증 3회 실패 - 재인증 요청");
          toast.error(t("PASS 인증에 3회 실패했습니다. 처음부터 다시 시작해주세요."));
          
          // 재인증 상태로 설정하고 처음으로 돌아가기
          setIsReauthentication(true);
          setPassVerificationAttempts(0); // 시도 횟수 초기화
          setCurrentStep(STEPS.PHONE_NUMBER);
        } else {
          // 재시도 안내
          toast.error(t(`PASS 인증에 실패했습니다. (${newAttempts}/${MAX_PASS_ATTEMPTS}) 다시 시도해주세요.`));
        }
      }
    });
  };

  // SMS 인증번호 확인
  const verifySmsCode = (smsCodeValue) => {
    // 매개변수가 없으면 상태에서 값을 가져옴
    const smsCode = smsCodeValue || formData.smsCode;

    devLog("[verifySmsCode] 시작", {
      smsCode,
      authData: {
        hasTokenData: !!authData.tokenData,
        hasMOKConfirmData: !!authData.MOKConfirmData
      }
    });

    // 인증번호가 비어있으면 체크
    if (!smsCode || smsCode.length !== 6) {
      devWarn("[verifySmsCode] 유효하지 않은 인증번호", { smsCode });
      toast.error(t("올바른 인증번호 6자리를 입력해주세요."));
      return;
    }

    setIsLoading(true);

    // 인증번호 확인용 데이터 구성
    let confirmData = {
      MOKConfirmData: authData.MOKConfirmData,
      session: authData.tokenData.session,
      authNumber: smsCode
    };

    devLog("[verifySmsCode] 확인 요청 데이터 준비", {
      confirmData,
      session: authData.tokenData?.session
    });

    // 인증번호 확인 API 호출
    devLog("[verifySmsCode] API 요청 시작");
    bffRequester.apiResult(confirmData, (result) => {
      devLog("[verifySmsCode] API 응답 받음", { result });
      setIsLoading(false);

      if (result.resultData) {
        // 인증 성공
        devLog("[verifySmsCode] 인증 성공", { resultData: result.resultData });
        toast.success(t("본인인증이 완료되었습니다."));

        // 인증 결과 데이터 저장
        setAuthData(prev => ({
          ...prev,
          verificationResult: result.resultData
        }));
        devLog("[verifySmsCode] 인증 결과 데이터 저장 완료");

        // 성인 여부 확인
        try {
          // 인증 결과에서 생년월일 정보 추출
          const userBirthday = result.resultData.userBirthday;
          devLog("[verifySmsCode] 생년월일 정보", { userBirthday });

          // 성인 여부 확인 - 기존에 import한 함수 사용
          const isAdultUser = isAdult(userBirthday);
          devLog("[verifySmsCode] 성인 여부 확인 결과", { isAdultUser, userBirthday });

          if (!isAdultUser) {
            // 성인이 아닌 경우
            devWarn("[verifySmsCode] 성인이 아님", { userBirthday });
            toast.error(t("성인만 가입할 수 있습니다."));
            setIsLoading(false);
            return;
          }

          // 성인인 경우 PIN 등록 단계로 이동
          devLog("[verifySmsCode] 성인 확인 완료, PIN 등록 단계로 이동");
          moveToNextStep(STEPS.PIN_REGISTRATION);
        } catch (error) {
          devError("[verifySmsCode] 성인 여부 확인 중 오류 발생:", error);
          devError("[verifySmsCode] 결과 데이터:", result.resultData);
          toast.error(t("본인 확인 중 오류가 발생했습니다. 다시 시도해주세요."));
        }
      } else {
        // 인증 실패
        devError("[verifySmsCode] 인증 실패", { result });
        toast.error(t("인증번호가 일치하지 않습니다. 다시 확인해주세요."));
      }
    });

    devLog("[verifySmsCode] 함수 실행 완료 (비동기 작업 진행 중)");
  };

  // PIN 번호 등록
  const registerPinNumber = () => {
    setIsLoading(true);
    devLog("[registerPinNumber] PIN 등록 시작");

    // BiometricService의 requestPinRegistration 메서드 활용
    biometricService.requestPinRegistration((success, pinHash) => {
      devLog("[registerPinNumber] PIN 등록 콜백 받음", { success, hasPinHash: !!pinHash });
      devLog("[registerPinNumber] PIN 등록 콜백 받음", { success, hasPinHash: pinHash });

      if (success && pinHash) {
        devLog("[registerPinNumber] PIN 등록 성공");

        // PIN 해시 값 상태에 저장
        setFormData(prev => ({ ...prev, pinHash: pinHash }));

        

        // 다음 단계로 이동
        setIsLoading(false);
        moveToNextStep(STEPS.BIOMETRIC_OPTION);
      } else {
        devError("[registerPinNumber] PIN 등록 실패");
        toast.error(t("PIN 등록에 실패했습니다. 다시 시도해주세요."));
        setIsLoading(false);
      }
    }).catch(error => {
      devError("[registerPinNumber] PIN 등록 오류:", error);
      toast.error(t("PIN 등록 중 오류가 발생했습니다."));
      setIsLoading(false);
    });
  };

  // 생체인증 등록
  const registerBiometric = async () => {
    if (!biometricStatus.supported) {
      toast.error(t("이 기기는 생체인증을 지원하지 않습니다"));
      return;
    }

    setIsLoading(true);

    try {
      // 최대 시도 횟수와 현재 시도 횟수 설정
      const maxAttempts = 3;
      let currentAttempt = 0;

      const attemptBiometricRegistration = async () => {
        try {
          currentAttempt++;
          devLog("[registerBiometric] 생체인증 등록 시도", { currentAttempt, maxAttempts });

          // 사용자 ID로 휴대폰 번호 사용
          const userId = formData.phoneNumber;

          // 생체인증 키 등록 진행
          const biometricKeyResult = await biometricService.registerBiometricKey(userId);
          devLog("[registerBiometric] 생체인증 키 등록 결과", { biometricKeyResult });

          if (biometricKeyResult && biometricKeyResult.publicKey) {
            // 생체인증 정보 처리를 위한 객체 생성 (formData 상태 업데이트 대신 직접 사용)
            const biometricData = {
              biometricPublicKey: biometricKeyResult.publicKey,
              biometricAlgorithm: biometricKeyResult.algorithm || "EC",
              biometricEnabled: true,
              biometricKeySignature: calculateKeySignature(biometricKeyResult.publicKey),
              biometricPlatform: biometricService.getPlatform()
            };

            devLog("[registerBiometric] 생체인증 정보 생성 완료", biometricData);
            toast.success(t("생체인증이 성공적으로 등록되었습니다!"), {
              autoClose: 2000,
              position: toast.POSITION.BOTTOM_CENTER
            });

            // 생체인증 데이터를 직접 전달하여 회원가입 진행
            completeRegistration(biometricData);
          } else {
            devError("[registerBiometric] 생체인증 키 생성 실패");
            if (currentAttempt < maxAttempts) {
              toast.warning(t("생체인증 키 생성에 실패했습니다. 다시 시도합니다.") + ` (${currentAttempt}/${maxAttempts})`, {
                autoClose: 2000,
                position: toast.POSITION.BOTTOM_CENTER
              });
              setTimeout(() => {
                attemptBiometricRegistration();
              }, 1500);
            } else {
              toast.error(t("생체인증 등록에 실패했습니다. 설정 메뉴에서 나중에 생체인증을 등록할 수 있습니다."), {
                autoClose: 3000,
                position: toast.POSITION.BOTTOM_CENTER
              });
              // 생체인증 없이 회원가입 진행
              completeRegistration();
            }
          }
        } catch (error) {
          devError("[registerBiometric] 생체인증 등록 오류:", error);
          if (currentAttempt < maxAttempts) {
            toast.warning(t("생체인증 등록 중 오류가 발생했습니다. 다시 시도합니다.") + ` (${currentAttempt}/${maxAttempts})`, {
              autoClose: 2000,
              position: toast.POSITION.BOTTOM_CENTER
            });
            setTimeout(() => {
              attemptBiometricRegistration();
            }, 1500);
          } else {
            toast.error(t("생체인증 등록에 실패했습니다. 설정 메뉴에서 나중에 생체인증을 등록할 수 있습니다."), {
              autoClose: 3000,
              position: toast.POSITION.BOTTOM_CENTER
            });
            // 생체인증 없이 회원가입 진행
            completeRegistration();
          }
        }
      };

      // 첫 번째 시도 시작
      await attemptBiometricRegistration();
    } catch (error) {
      devError("[registerBiometric] 최종 오류:", error);
      toast.error(t("생체인증 등록 중 오류가 발생했습니다."));
      setIsLoading(false);
    }
  };

  // 🔥 공개키 해시 계산 함수 (HamburgerMenu와 동일)
  const calculateKeySignature = (publicKeyBase64) => {
    try {
      // Base64 인코딩된 공개키의 앞부분만 사용하여 간단한 서명 생성
      return publicKeyBase64.substring(0, 64);
    } catch (error) {
      devError("[calculateKeySignature] 키 시그니처 생성 오류:", error);
      return publicKeyBase64; // 오류 시 원본 키 반환
    }
  };

  // 등록 완료 및 로그인 처리
  const completeRegistration = (biometricData = null) => {
    setIsLoading(true);
    devLog("[completeRegistration] 시작", {
      isReauthentication,
      authData: { hasVerificationResult: !!authData.verificationResult },
      formData: {
        phoneNumber: formData.phoneNumber,
        name: formData.name,
        hasPinHash: !!formData.pinHash
      },
      biometricData: biometricData
    });

    // 🔥 재인증 상태인 경우 PIN 업데이트만 수행
    if (isReauthentication) {
      devLog("[completeRegistration] 🔥 재인증 상태 - PIN 업데이트 진행");
      handleReauthenticationPinUpdate(biometricData);
      return;
    }

    // 🔥 회원 상태 확인 후 신규/기존 분기 처리
    devLog("[completeRegistration] 회원 상태 확인 시작");
    
    let mobileDuplicateData = { 
      mobileNumber: formData.phoneNumber.replace(/-/g, ''), 
      storeId: "root", 
      role: "MEMBER" 
    };

    requester.membermobileNumDuplicate(mobileDuplicateData, (duplicateResult) => {
      if (duplicateResult.code === 0) {
        if (duplicateResult.data) {
          // 🔥 기존 회원 (활성) - PIN 업데이트 후 로그인
          devLog("[completeRegistration] 기존 회원 확인 - PIN 업데이트 진행");
          updateExistingMemberPin(biometricData);
        } else {
          // 🔥 false인 경우 - 탈퇴 여부 추가 확인
          devLog("[completeRegistration] 탈퇴 여부 확인 시작");
          let userYnConfirmData = {
            userName: formData.phoneNumber.replace(/-/g, ''),
          };
          requester.useYnConfirm(userYnConfirmData, (useYnResult) => {
            devLog(useYnResult);
            if (useYnResult.code === 0) {
              if (useYnResult.data) {
                // 🔥 탈퇴한 회원 - restore 후 로그인
                devLog("[completeRegistration] 탈퇴한 회원 확인 - restore 후 로그인 진행");
                handleWithdrawnMemberRestore(biometricData);
              } else {
                // 🔥 진짜 신규 회원 - 회원가입 진행
                devLog("[completeRegistration] 신규 회원 확인 - 회원가입 진행");
                proceedWithNewMemberSignUp(biometricData);
              }
            } else {
              // 🔥 useYnConfirm API 오류 시 신규 회원으로 처리
              devWarn("[completeRegistration] useYnConfirm API 오류, 신규 회원으로 처리:", useYnResult);
              proceedWithNewMemberSignUp(biometricData);
            }
          });
        }
      } else {
        devError("[completeRegistration] 회원 확인 API 오류:", duplicateResult);
        toast.error(t("회원 확인 중 오류가 발생했습니다."));
        setIsLoading(false);
      }
    });
  };

  // 🔥 신규 회원 회원가입 처리 함수
  const proceedWithNewMemberSignUp = (biometricData) => {
    devLog("[proceedWithNewMemberSignUp] 신규 회원가입 진행");

    // 회원가입 API 호출
    let signUpData = {
      storeId: "root",
      mobileNumber: formData.phoneNumber,
      name: formData.name,
      birthDay: authData.verificationResult.userBirthday,
      ci: authData.verificationResult.ci,
      di: authData.verificationResult.di,
      pinHash: ""
    };

    const platform = biometricService.getPlatform();
    // PIN 해시 값 추출 및 처리
    try {
      if (typeof formData.pinHash === 'string') {
        if (platform === 'android') {
          // Android: JSON 파싱 시도
          const parsedPin = JSON.parse(formData.pinHash);
          signUpData.pinHash = parsedPin.pinHash || parsedPin.hashedPin || formData.pinHash;
        } else {
          // iOS: 바로 사용
          signUpData.pinHash = formData.pinHash;
        }
      } else if (formData.pinHash && typeof formData.pinHash === 'object') {
        // 객체인 경우 직접 접근
        signUpData.pinHash = formData.pinHash.pinHash || formData.pinHash.hashedPin;
      } else {
        devError("[completeRegistration] 유효한 PIN 해시 값을 찾을 수 없습니다:", formData.pinHash);
      }
    } catch (error) {
      devError("[completeRegistration] PIN 해시 처리 중 오류:", error);
      devError("[completeRegistration] 원본 PIN 데이터:", formData.pinHash);
    }

    devLog("[completeRegistration] 회원가입 데이터", signUpData);

    // 직접 전달받은 생체인증 데이터가 있으면 추가
    if (biometricData) {
      devLog("[completeRegistration] 직접 전달된 생체인증 데이터 추가됨", biometricData);
      signUpData = {
        ...signUpData,
        biometricPublicKey: biometricData.biometricPublicKey,
        biometricAlgorithm: biometricData.biometricAlgorithm,
        biometricEnabled: true,
        biometricKeySignature: biometricData.biometricKeySignature,
        biometricPlatform: biometricData.biometricPlatform
      };
    } else {
      devLog("[completeRegistration] 생체인증 데이터 없음!");
    }

    devLog("[completeRegistration] 최종 회원가입 데이터", signUpData);

    requester.memberNumberSignUp(signUpData, (result) => {
      devLog("[proceedWithNewMemberSignUp] 회원가입 응답", { resultCode: result.code });

      if (result.code === 0) {
        // 회원가입 성공, 로그인 처리
        devLog("[proceedWithNewMemberSignUp] 회원가입 성공, 로그인 시작");
        performLogin();
      } else {
        setIsLoading(false);
        devError("[proceedWithNewMemberSignUp] 회원가입 실패", { resultCode: result.code });
        toast.error(t("회원가입 중 오류가 발생했습니다"));
      }
    });
  };

  // 🔥 기존 회원 PIN 업데이트 처리 함수
  const updateExistingMemberPin = (biometricData) => {
    devLog("[updateExistingMemberPin] 기존 회원 PIN 업데이트 시작");
    
    const platform = biometricService.getPlatform();
    // PIN 해시 값 추출
    let pinHashValue = "";
    try {
      if (typeof formData.pinHash === 'string') {
        if (platform === 'android') {
          // Android: JSON 파싱 시도
          const parsedPin = JSON.parse(formData.pinHash);
          pinHashValue = parsedPin.pinHash || parsedPin.hashedPin || formData.pinHash;
        } else {
          // iOS: 바로 사용
          pinHashValue = formData.pinHash;
        }
      } else if (formData.pinHash && typeof formData.pinHash === 'object') {
        pinHashValue = formData.pinHash.pinHash || formData.pinHash.hashedPin;
      }
    } catch (error) {
      devError("[updateExistingMemberPin] PIN 해시 처리 오류:", error);
    }

    // PIN 업데이트 API 호출
    const pinUpdateData = {
      userName: formData.phoneNumber,
      pinHash: pinHashValue
    };

    devLog("[updateExistingMemberPin] PIN 업데이트 요청:", pinUpdateData);

    requester.registerPinByUserName(pinUpdateData, (result) => {
      devLog("[updateExistingMemberPin] PIN 업데이트 응답:", result);

      if (result.code === 0) {
        devLog("[updateExistingMemberPin] PIN 업데이트 성공");

        // 생체인증 데이터가 있으면 생체인증도 업데이트
        if (biometricData) {
          devLog("[updateExistingMemberPin] 생체인증 데이터 업데이트 진행");

          const updateData = {
            userName: formData.phoneNumber,
            biometricPublicKey: biometricData.biometricPublicKey,
            biometricAlgorithm: biometricData.biometricAlgorithm,
            biometricEnabled: true,
            biometricKeySignature: biometricData.biometricKeySignature,
            biometricPlatform: biometricData.biometricPlatform
          };

          requester.checkMemberBiometricStatus(updateData, (biometricResult) => {
            if (biometricResult.code === 0) {
              devLog("[updateExistingMemberPin] 생체인증 업데이트 성공");
              localStorage.setItem('puffu_biometric_supported', 'true');
            } else {
              devError("[updateExistingMemberPin] 생체인증 업데이트 실패", biometricResult);
            }
            // 생체인증 성공/실패와 무관하게 로그인 진행
            performLogin();
          });
        } else {
          // 생체인증 없이 로그인 진행
          performLogin();
        }
      } else {
        setIsLoading(false);
        devError("[updateExistingMemberPin] PIN 업데이트 실패:", result);
        toast.error(t("PIN 업데이트 중 오류가 발생했습니다"));
      }
    });
  };

  // 특정 단계로 돌아가는 함수에서 타이머 제거하여 즉시 적용
  const goBackToStep = (step) => {
    // 수정 모드로 설정 (이전 단계로 돌아갈 때는 수정을 위한 것으로 간주)
    setIsEditMode(true);

    // 해당 단계보다 높은 단계의 완료 정보를 삭제
    // 단, 통신사 단계로 돌아갈 때는 즉시 완료된 단계 정보 업데이트를 위해 해당 단계 정보는 유지
    const updatedCompletedSteps = { ...completedSteps };

    // 선택한 단계보다 높은 단계의 정보만 삭제 (같은 단계는 유지)
    for (let i = step + 1; i <= Math.max(...Object.keys(completedSteps).map(Number)); i++) {
      delete updatedCompletedSteps[i];
    }

    setCompletedSteps(updatedCompletedSteps);
    setCurrentStep(step);

    // 단계에 따른 추가 처리
    if (step === STEPS.MOBILE_CARRIER && formData.carrierCode) {
      // 통신사 단계로 돌아갈 때 선택된 통신사 정보가 있으면 해당 정보를 스택에서 즉시 제거
      const updatedSteps = { ...updatedCompletedSteps };
      delete updatedSteps[STEPS.MOBILE_CARRIER];
      setCompletedSteps(updatedSteps);
    }
  };

  // 이전 단계로 이동하는 함수
  const goToPrevStep = (step) => {
    // 키보드 숨기기
    hideNativeKeyboard();

    // 특정 단계가 지정된 경우 해당 단계로 이동
    if (typeof step === 'number') {
      goBackToStep(step);
      return;
    }

    // 현재 단계가 첫 단계가 아닌 경우에만 이전 단계로 이동
    if (currentStep > 0) {
      // 완료된 상태에서 현재 단계 제거
      const updatedCompletedSteps = { ...completedSteps };
      delete updatedCompletedSteps[currentStep - 1];
      setCompletedSteps(updatedCompletedSteps);

      // 이전 단계로 이동
      setCurrentStep(prev => prev - 1);
    }
  };

  // 약관 모달 표시 함수
  const showTerms = (type) => {
    if (type === 'terms') {
      setShowTermsModal(true);
    } else if (type === 'privacy') {
      setShowPrivacyModal(true);
    }
  };

  // 모달 닫기 함수
  const closeModal = () => {
    setShowTermsModal(false);
    setShowPrivacyModal(false);
  };

  // 뒤로가기 버튼 렌더링 함수
  const renderBackButton = () => {
    if (currentStep === 0) return null;

    return (
      <button
        className={styles.backButton}
        onClick={() => goToPrevStep()}
        type="button"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    );
  };

  // 완료된 단계 헤더 렌더링 함수 - 클릭 시 해당 단계로 이동하도록 수정
  const renderCompletedStepHeaders = () => {
    const stepsToRender = [];

    // 현재 단계보다 이전 단계들만 렌더링
    for (let i = 0; i < currentStep; i++) {
      if (completedSteps[i]) {
        stepsToRender.push(
          <div
            key={i}
            className={styles.completedStepHeader}
            onClick={() => {
              // 수정 버튼 클릭 시 이전 전화번호 값도 초기화
              if (i === STEPS.PHONE_NUMBER) {
                prevPhoneNumber.current = formData.phoneNumber;
              }
              goBackToStep(i);
            }}
          >
            <div className={styles.completedStepTitle}>{completedSteps[i].title}</div>
            <div className={styles.completedStepValue}>{completedSteps[i].value}</div>
            <div className={styles.editIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.293 2.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1 0 1.414l-13 13A1 1 0 0 1 8 21H4a1 1 0 0 1-1-1v-4a1 1 0 0 1 .293-.707l13-13z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
          </div>
        );
      }
    }

    return stepsToRender;
  };

  // 각 단계별 렌더링 함수들
  // 🔥 인증 방식 선택 화면 렌더링
  const renderAuthenticationMethodStep = () => (
    <div className={styles.formStep}>
      <div className={styles.stepHeader}>
        <h2 className={styles.stepTitle}>{t("본인인증 방식 선택")}</h2>
      </div>

      <p className={styles.stepDescription}>
        {t("원하시는 본인인증 방식을 선택해주세요")}
      </p>

      <div className={styles.methodSelectionContainer}>
        <div 
          className={`${styles.methodCard} ${formData.authenticationMethod === 'PASS' ? styles.selected : ''}`}
          onClick={() => handleInputChange("authenticationMethod", "PASS")}
        >
          <div className={styles.methodIcon}>
            <p>PASS</p>
          </div>
          <div className={styles.methodInfo}>
            <h3>{t("PASS 인증")}</h3>
            <p>{t("빠르고 간편한 인증")}</p>
            
          </div>
        </div>

        <div 
          className={`${styles.methodCard} ${formData.authenticationMethod === 'SMS' ? styles.selected : ''}`}
          onClick={() => handleInputChange("authenticationMethod", "SMS")}
        >
          <div className={styles.methodIcon}>💬</div>
          <div className={styles.methodInfo}>
            <h3>{t("SMS 인증")}</h3>
            <p>{t("문자 인증으로 안전하게")}</p>
            
          </div>
        </div>
      </div>

      <button
        className={styles.actionButton}
        onClick={handleNextStep}
        disabled={isLoading || !formData.authenticationMethod}
      >
        {t("다음")}
      </button>
    </div>
  );

  const renderPhoneNumberStep = () => (
    <div className={styles.formStep}>
      <div className={styles.stepHeader}>
        {/* {renderBackButton()} */}
        <h2 className={styles.stepTitle}>{t("휴대폰 번호 입력")}</h2>
      </div>

      {/* <p className={styles.stepDescription}>
        {t("휴대폰 번호를 입력해주세요")}
      </p> */}

      <div className={styles.inputWrapper} style={{ marginBottom: '24px' }}>
        <SecureInput
          regExp={[mobileNoFormat]}
          type={"number"}
          id="phoneNumber"
          ref={el => (inputs.current[0] = el)}
          placeHolder={t("휴대폰 번호")}
          phone={true}
          value={formData.phoneNumber}
          onChange={(value) => handleInputChange("phoneNumber", value)}
          onEnterPress={() => {
            // 이미 11자리이면 다음 단계로 진행
            const numericValue = formData.phoneNumber.replace(/-/g, '');
            if (numericValue.length === 11) {
              // 버튼 클릭 효과 추가
              const actionButton = document.querySelector(`.${styles.actionButton}`);
              if (actionButton) {
                actionButton.classList.add(styles.buttonPressed);
                setTimeout(() => {
                  actionButton.classList.remove(styles.buttonPressed);
                  checkExistingMember();
                }, 150);
              } else {
                checkExistingMember();
              }
            }
          }}
          maxLength={13} // 하이픈 포함 길이
          autoFocus={true} // 자동 포커스 설정
        />
      </div>

      <button
        className={styles.actionButton}
        onClick={handleNextStep}
        disabled={isLoading || formData.phoneNumber.replace(/-/g, '').length !== 11}
      >
        {isLoading ? t("확인 중...") : t("다음")}
      </button>
    </div>
  );

  const renderResidentNumberStep = () => (
    <div className={styles.formStep}>
      <div className={styles.stepHeader}>
        {/* {renderBackButton()} */}
        <h2 className={styles.stepTitle}>{t("주민등록번호 입력")}</h2>
      </div>

      <p className={styles.stepDescription}>
        {t("주민등록번호")}
      </p>

      {/* 주민등록번호 입력 필드 개선 */}
      <div className={styles.residentNumberContainer}>
        {/* 앞 6자리 */}
        <div className={`${styles.inputWrapper} ${styles.residentNumberFront}`}>
          <SecureInput
            type={"number"}
            id="residentNumberFront"
            maxLength={6}
            ref={el => (inputs.current[1] = el)}
            placeHolder={t("앞 6자리")}
            number={true}
            value={formData.residentNumberFront}
            onChange={(value) => handleInputChange("residentNumberFront", value)}
            autoFocus={currentStep === STEPS.RESIDENT_NUMBER}
          />
        </div>

        {/* 구분선 */}
        <div className={styles.inputDivider}>-</div>

        {/* 뒷 7자리 입력과 마스킹 도트를 포함한 컨테이너 */}
        <div className={styles.residentNumberBackContainer}>
          {/* 뒷 첫자리 입력 */}
          <div className={styles.residentNumberBack}>
            <SecureInput
              type={"number"}
              id="residentNumberBack"
              maxLength={1}
              ref={el => (inputs.current[2] = el)}
              placeHolder={""}
              number={true}
              value={formData.residentNumberBack.charAt(0) || ""}
              onChange={(value) => {
                // 첫 번째 자리만 저장하고 나머지는 자동으로 '******'로 채움
                handleInputChange("residentNumberBack", value + "******".substring(0, 6));
              }}
              style={{ width: '40px', paddingRight: '0', textAlign: 'center' }}
            />
          </div>

          {/* 도트 표시 영역 */}
          <div className={styles.dotsContainer}>
            {Array(6).fill(0).map((_, idx) => (
              <span key={idx} className={styles.maskedDot} />
            ))}
          </div>
        </div>
      </div>

      <button
        className={styles.actionButton}
        onClick={handleNextStep}
        disabled={isLoading || !(formData.residentNumberFront.length === 6 && formData.residentNumberBack.length >= 1)}
      >
        {t("다음")}
      </button>
    </div>
  );

  const renderMobileCarrierStep = () => (
    <div className={styles.formStep}>
      <div className={styles.stepHeader}>
        {/* {renderBackButton()} */}
        <h2 className={styles.stepTitle}>{t("통신사 선택")}</h2>
      </div>

      <p className={styles.stepDescription}>
        {t("사용 중인 통신사를 선택해주세요")}
      </p>

      <div className={styles.selectWrapper}>
        <select
          id="carrierSelect"
          className={`${styles.select} ${formData.carrierCode ? styles.selectedValue : ''}`}
          value={formData.carrierCode}
          onChange={(e) => handleInputChange("carrierCode", e.target.value)}
          style={{
            fontSize: '17px',
            color: formData.carrierCode ? '#333' : '#999',
            fontWeight: formData.carrierCode ? '500' : 'normal',
            transition: 'all 0.2s ease'
          }}
          // focus 대신 autoFocus 속성 사용 (안드로이드 웹뷰에서 더 안정적)
          autoFocus={true}
          // 터치 디바이스에서 클릭 오류 방지를 위한 추가 설정
          onClick={(e) => {
            // 클릭 이벤트 전파 중지 제거
            // e.stopPropagation();
          }}
        >
          <option value="" disabled>{t("통신사 선택")}</option>
          {CARRIERS.map(carrier => (
            <option key={carrier.id} value={carrier.id} style={{ color: '#333' }}>
              {carrier.name}
            </option>
          ))}
        </select>
      </div>

      <button
        className={styles.actionButton}
        onClick={handleNextStep}
        disabled={isLoading || !formData.carrierCode}
      >
        {t("다음")}
      </button>
    </div>
  );

  const renderNameStep = () => (
    <div className={styles.formStep}>
      <div className={styles.stepHeader}>
        {/* {renderBackButton()} */}
        <h2 className={styles.stepTitle}>{t("이름")}</h2>
      </div>

      <p className={styles.stepDescription}>
        {t("이름을 입력해주세요")}
      </p>

      <div className={styles.inputWrapper}>
        <SecureInput
          id="nameInput"
          ref={el => (inputs.current[3] = el)}
          placeHolder={t("이름")}
          value={formData.name}
          onChange={(value) => handleInputChange("name", value)}
          onEnterPress={() => {
            // 버튼 클릭 효과 추가
            const actionButton = document.querySelector(`.${styles.actionButton}`);
            if (actionButton) {
              actionButton.classList.add(styles.buttonPressed);
              setTimeout(() => {
                actionButton.classList.remove(styles.buttonPressed);
                handleNextStep();
              }, 150);
            } else {
              handleNextStep();
            }
          }}
          onFocus={() => {
            const isAndroid = /Android/i.test(navigator.userAgent);
            if (!isAndroid) return;

            setTimeout(() => {
              const nameInput = document.getElementById('nameInput');
              if (nameInput) {
                const rect = nameInput.getBoundingClientRect();
                const elementBottom = rect.bottom;
                const viewportHeight = window.innerHeight;
                const estimatedKeyboardHeight = viewportHeight * 0.4;

                if (elementBottom > viewportHeight - estimatedKeyboardHeight) {
                  nameInput.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                  });
                }
              }
            }, 300);
          }}
          autoFocus={currentStep === STEPS.NAME}
        />
      </div>

      <button
        className={styles.actionButton}
        onClick={handleNextStep}
        disabled={isLoading || !formData.name.trim()}
      >
        {t("다음")}
      </button>
    </div>
  );

  const renderTermsStep = () => (
    <div className={styles.formStep}>
      <div className={styles.stepHeader}>
        {/* {renderBackButton()} */}
        <h2 className={styles.stepTitle}>{t("약관 동의")}</h2>
      </div>

      <div className={styles.agreementArea}>
        <div className={styles.checkboxWrapper}>
          <input
            type="checkbox"
            id="terms"
            checked={formData.termsAgreed}
            onChange={() => handleCheckboxChange("termsAgreed")}
            className={styles.checkbox}
          />
          <label
            htmlFor="terms"
            className={styles.checkmark}
            onClick={(e) => e.stopPropagation()}
          />
          <span className={styles.agreementText}>
            {t("이용약관에 동의합니다 (필수)")}
          </span>
          <button className={styles.viewButton} onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            showTerms('terms');
          }}>
            {t("보기")}
          </button>
        </div>
      </div>

      <div className={styles.agreementArea}>
        <div className={styles.checkboxWrapper}>
          <input
            type="checkbox"
            id="privacy"
            checked={formData.privacyAgreed}
            onChange={() => handleCheckboxChange("privacyAgreed")}
            className={styles.checkbox}
          />
          <label
            htmlFor="privacy"
            className={styles.checkmark}
            onClick={(e) => e.stopPropagation()}
          />
          <span className={styles.agreementText}>
            {t("개인정보처리방침에 동의합니다 (필수)")}
          </span>
          <button className={styles.viewButton} onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            showTerms('privacy');
          }}>
            {t("보기")}
          </button>
        </div>
      </div>

      <button
        className={styles.actionButton}
        onClick={handleNextStep}
        disabled={isLoading}
      >
        {isLoading ? t("인증번호 요청 중...") : t("인증번호 요청")}
      </button>

      {/* 약관 모달 */}
      {showTermsModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div style={{ width: "10%" }} />
              <h3>{t("이용약관")}</h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>×</button>
            </div>
            <div className={styles.modalBody}>
              <PolicyPage btn={true} />
            </div>
            {/* <div className={styles.modalFooter}>
              <button className={styles.modalButton} onClick={closeModal}>
                {t("확인")}
              </button>
            </div> */}
          </div>
        </div>
      )}

      {/* 개인정보처리방침 모달 */}
      {showPrivacyModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div style={{ width: "10%" }} />
              <h3>{t("개인정보처리방침")}</h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>×</button>
            </div>
            <div className={styles.modalBody}>
              <PrivacyPolicy btn={true} />
            </div>
            {/* <div className={styles.modalFooter}>
              <button className={styles.modalButton} onClick={closeModal}>
                {t("확인")}
              </button>
            </div> */}
          </div>
        </div>
      )}
    </div>
  );

  // 🔥 PASS 인증 화면 렌더링
  const renderPassVerificationStep = () => (
    <div className={styles.formStep}>
      <div className={styles.stepHeader}>
        <h2 className={styles.stepTitle}>{t("PASS 인증")}</h2>
      </div>

      <p className={styles.stepDescription}>
        {t("PASS 앱에서 본인인증을 완료해주세요")}
      </p>

      

      <button
        className={styles.actionButton}
        onClick={handleNextStep}
        disabled={isLoading}
      >
        {isLoading ? t("인증 확인 중...") : t("PASS 인증 완료")}
      </button>
    </div>
  );

  const renderSmsVerificationStep = () => (
    <div className={styles.formStep}>
      <div className={styles.stepHeader}>
        {/* {renderBackButton()} */}
        <h2 className={styles.stepTitle}>{t("인증번호 확인")}</h2>
      </div>

      <p className={styles.stepDescription}>
        {t("휴대폰으로 전송된 인증번호를 입력해주세요")}
      </p>

      <div className={styles.inputWrapper}>
        <SecureInput
          autoComplete={"one-time-code"}
          number={true}
          id="smsCode"
          inputmode="numeric"
          maxLength={6}
          ref={el => (inputs.current[4] = el)}
          placeHolder={t("인증번호 6자리")}
          value={formData.smsCode}
          onChange={(value) => handleInputChange("smsCode", value)}
          onEnterPress={handleNextStep}
          autoFocus={currentStep === STEPS.SMS_VERIFICATION}
        />
      </div>

      <div className={styles.timerContainer}>
        {isTimerActive ? (
          <div className={styles.timer}>{formatTime(timeRemaining)}</div>
        ) : (
          <button
            className={styles.resendButton}
            onClick={resendSmsCode}
            disabled={isLoading}
          >
            {t("인증번호 재전송")}
          </button>
        )}
      </div>

      <button
        className={styles.actionButton}
        onClick={handleNextStep}
        disabled={isLoading}
      >
        {isLoading ? t("확인 중...") : t("확인")}
      </button>
    </div>
  );

  const renderPinRegistrationStep = () => (
    <div className={styles.formStep}>
      <div className={styles.stepHeader}>
        {/* {renderBackButton()} */}
        <h2 className={styles.stepTitle}>{t("PIN 번호 등록")}</h2>
      </div>

      <p className={styles.stepDescription}>
        {t("빠른 로그인을 위한 6자리 PIN 번호를 등록해주세요")}
      </p>

      <div className={styles.pinInfo}>
        <p>{t("PIN 번호는 앱 로그인에 사용됩니다.")}</p>
        <p>{t("PIN 번호는 기기에 안전하게 저장됩니다.")}</p>
      </div>

      <button
        className={styles.actionButton}
        onClick={handleNextStep}
        disabled={isLoading}
      >
        {isLoading ? t("등록 중...") : t("PIN 번호 등록하기")}
      </button>
    </div>
  );

  const renderBiometricOptionStep = () => (
    <div className={styles.formStep}>
      <div className={styles.stepHeader}>
        {/* {renderBackButton()} */}
        <h2 className={styles.stepTitle}>
          {isReauthenticationComplete ? t("생체인증 재등록") : t("생체인증 등록")}
        </h2>
      </div>

      <p className={styles.stepDescription}>
        {isReauthenticationComplete
          ? t("PIN이 변경되었습니다. 생체인증을 다시 등록하시겠습니까?")
          : t("더 빠른 로그인을 위해 생체인증을 등록하시겠습니까?")
        }
      </p>

      {!biometricStatus.supported && (
        <p className={styles.notSupportedText}>
          {t("이 기기는 생체인증을 지원하지 않습니다.")}
        </p>
      )}

      <div className={styles.buttonGroup}>
        {biometricStatus.supported && (
          <button
            className={styles.actionButton}
            onClick={() => {
              // 🔥 디버깅 로그 추가
              devLog("🔍 생체인증 '등록하기' 버튼 클릭 디버깅:", {
                isReauthenticationComplete,
                isReauthentication,
                currentStep,
                isExistingMember,
                willCall: isReauthenticationComplete ? 'registerBiometricForReauth' : 'registerBiometric'
              });

              // 🔥 더 정확한 조건 분기 로직
              const isDefinitelyReauth = (
                isReauthentication && 
                isReauthenticationComplete && 
                isExistingMember &&
                currentStep === 'biometricOption'
              );

              // 🔥 안전한 조건부 실행
              if (isDefinitelyReauth) {
                devLog("✅ 확실한 재인증 상황 - registerBiometricForReauth 호출");
                registerBiometricForReauth();
              } else {
                devLog("✅ 신규회원 또는 애매한 상황 - registerBiometric 호출");
                registerBiometric();
              }
            }}
            disabled={isLoading}
          >
            {isLoading ? t("등록 중...") : t("생체인증 등록하기")}
          </button>
        )}

        <button
          className={`${styles.actionButton} ${styles.secondaryButton}`}
          onClick={() => {
            // 🔥 디버깅 로그 추가
            devLog("🔍 생체인증 '다음에 하기' 버튼 클릭 디버깅:", {
              isReauthenticationComplete,
              isReauthentication,
              currentStep,
              hasFormData: {
                phoneNumber: !!formData.phoneNumber,
                name: !!formData.name,
                residentNumber: !!formData.residentNumber
              },
              isExistingMember,
              authDataExists: !!authData.verificationResult
            });

            // 🔥 더 정확한 조건 분기 로직
            const isDefinitelyReauth = (
              isReauthentication && 
              isReauthenticationComplete && 
              isExistingMember &&
              currentStep === 'biometricOption'
            );

            const isDefinitelyNewMember = (
              !isReauthentication && 
              authData.verificationResult && // SMS/PASS 인증 완료
              formData.name && 
              formData.residentNumber
            );

            devLog("🔍 판별 결과:", {
              isDefinitelyReauth,
              isDefinitelyNewMember,
              willCall: isDefinitelyReauth ? 'performLogin' : 'completeRegistration'
            });

            // 🔥 안전한 조건부 실행
            if (isDefinitelyReauth) {
              devLog("✅ 확실한 재인증 상황 - performLogin 호출");
              performLogin();
            } else {
              devLog("✅ 신규회원 또는 애매한 상황 - completeRegistration 호출 (안전장치)");
              completeRegistration();
            }
          }}
          disabled={isLoading}
        >
          {t("다음에 하기")}
        </button>
      </div>
    </div>
  );

  // PIN 검증 화면 렌더링 함수 수정
  const renderPinVerificationStep = () => {
    // 상태에 따른 메시지 결정
    const getStatusMessage = () => {
      switch (pinVerificationState) {
        case 'verifying':
          return t("PIN 검증 중...");
        case 'server_verifying':
          return t("서버 검증 중...");
        case 'failed':
          return t("PIN이 일치하지 않습니다");
        case 'ready_for_retry':
        case 'reset': // 🔥 reset 상태 추가 (네이티브와 통일)
          return t("PIN 번호를 입력해주세요");
        case 'idle':
        default:
          return t("PIN 번호를 입력해주세요");
      }
    };

    // 상태에 따른 버튼 텍스트 결정
    const getButtonText = () => {
      switch (pinVerificationState) {
        case 'verifying':
          return t("검증 중...");
        case 'server_verifying':
          return t("서버 검증 중...");
        case 'failed':
          return t("재시도");
        case 'ready_for_retry':
        case 'reset': // 🔥 reset 상태 추가 (네이티브와 통일)
          return t("확인");
        case 'idle':
        default:
          return t("확인");
      }
    };

    // 상태에 따른 버튼 비활성화 결정
    const isButtonDisabled = () => {
      return pinVerificationState === 'verifying' ||
        pinVerificationState === 'server_verifying' ||
        isLoading;
    };

    // 상태에 따른 스타일 클래스 결정
    const getStatusClass = () => {
      switch (pinVerificationState) {
        case 'failed':
          return styles.errorMessage;
        case 'verifying':
        case 'server_verifying':
          return styles.verifyingMessage;
        default:
          return styles.normalMessage;
      }
    };

    return (
      <div className={styles.formStep}>
        <div className={styles.stepHeader}>
          <h2 className={styles.stepTitle}>{t("PIN 번호 입력")}</h2>
        </div>

        {/* 상태에 따른 동적 메시지 표시 */}
        <p className={`${styles.stepDescription} ${getStatusClass()}`}>
          {getStatusMessage()}
        </p>

        {/* 시도 횟수 표시 (실패 상태일 때만) */}
        {pinVerificationState === 'failed' && pinVerificationAttempts > 0 && (
          <p className={styles.attemptCounter}>
            {t("시도 횟수")}: {pinVerificationAttempts}/{MAX_PIN_ATTEMPTS}
          </p>
        )}

        <div className={styles.inputWrapper}>
          <SecureInput
            type="number"
            id="pinVerification"
            maxLength={6}
            ref={el => (inputs.current[5] = el)}
            placeHolder={t("PIN 번호 6자리")}
            value={formData.pinNumber}
            onChange={(value) => handleInputChange("pinNumber", value)}
            onEnterPress={() => {
              if (!isButtonDisabled()) {
                handlePinVerification();
              }
            }}
            onClick={() => {
              if (!isButtonDisabled() && !pinVerificationManager.isCurrentlyVerifying()) {
                handlePinVerification();
              }
            }}
            readOnly={true}
            disabled={isButtonDisabled()}
          />
        </div>

        <button
          className={styles.actionButton}
          onClick={handlePinVerification}
          disabled={isButtonDisabled()}
        >
          {getButtonText()}
        </button>

        {/* 재시도 안내 메시지 (실패 상태일 때만) */}
        {pinVerificationState === 'ready_for_retry' && (
          <p className={styles.retryMessage} style={{ color: 'red' }}>
            {t("다시 PIN 번호를 입력해주세요")}
          </p>
        )}

      </div>
    );
  };

  // 서버에 PIN 검증 요청하는 함수
  const verifyPinWithServer = async (pinNumber) => {
    devLog("[verifyPinWithServer] 서버 PIN 검증 시작", { phoneNumber: formData.phoneNumber });

    return new Promise((resolve, reject) => {
      try {
        // 서버 검증 요청 데이터 구성
        const verifyData = {
          userName: formData.phoneNumber,
          pinNumber: pinNumber
        };

        devLog("[verifyPinWithServer] 서버 PIN 검증 요청 데이터:", verifyData);

        // 서버에 검증 요청
        requester.verifyPin(verifyData, (result) => {
          devLog("[verifyPinWithServer] 서버 PIN 검증 응답:", result);

          if (result.code === 0 && result.data === true) {
            devLog("[verifyPinWithServer] 서버 PIN 검증 성공");
            resolve(true);
          } else {
            devLog("[verifyPinWithServer] 서버 PIN 검증 실패");
            resolve(false);
          }
        });
      } catch (error) {
        devError("[verifyPinWithServer] 서버 PIN 검증 오류:", error);
        reject(error);
      }
    });
  };

  // PIN 검증 처리 (새로운 매니저 사용)
  const handlePinVerification = async () => {
    devLog("==============================================================");
    devLog("[PIN 검증] handlePinVerification 시작");
    devLog("[PIN 검증] 🔥🔥🔥 PIN 다이얼로그 호출 시도 🔥🔥🔥");
    devLog("[PIN 검증] 현재 상태:", {
      isVerifying: pinVerificationManager.isCurrentlyVerifying(),
      attempts: pinVerificationAttempts,
      pinVerificationState,
      currentStep,
      formData_phoneNumber: formData.phoneNumber,
      isExistingMember
    });

    if (pinVerificationManager.isCurrentlyVerifying() && pinVerificationState !== 'ready_for_retry' && pinVerificationState !== 'reset') {
      devLog("[PIN 검증] 이미 검증 중입니다. 재시도 차단");
      return;
    }

    devLog("[PIN 검증] 🚀 PIN 검증 프로세스 시작");
    setIsLoading(true);

    // 콜백 함수들 정의
    const callbacks = {
      onSuccess: () => {
        devLog("[PIN 검증] ✅ 검증 성공 - 로그인 진행");
        pinVerificationManager.resetAttempts();
        setPinVerificationAttempts(0);
        handleLogin();
      },

      onFailure: (message, shouldRedirect = false) => {
        devLog("[PIN 검증] ❌ 검증 실패:", message);
        toast.error(t(message));

        if (shouldRedirect) {
          // 🔥 최대 시도 횟수 초과 시 재인증 필요 - 재인증 플래그 설정
          devLog("[PIN 검증] 🔥 5회 실패로 인한 재인증 - 재인증 플래그 설정");
          setIsReauthentication(true); // 재인증 상태로 설정
          setCurrentStep(STEPS.PHONE_NUMBER);
          pinVerificationManager.resetAttempts();
          setPinVerificationAttempts(0);
          devLog("[PIN 검증] 🔥 재인증 플래그 설정 완료, 전화번호 입력 단계로 이동");
        } else {
          // 시도 횟수 업데이트
          setPinVerificationAttempts(pinVerificationManager.verificationAttempts);
        }
      },

      onError: (error) => {
        devError("[PIN 검증] 💥 오류:", error);
        devLog("[PIN 검증] 🔥 onError에서 reset 상태로 변경");
        setPinVerificationState('reset'); // 🔥 idle → reset으로 변경
        toast.error(t("PIN 검증 중 오류가 발생했습니다. 다시 시도해주세요."));
      },

      onStateChange: (state, message = "") => {
        devLog(`[PIN 검증] 🔥🔥🔥 상태 변경 요청: ${state} - ${message} 🔥🔥🔥`);
        devLog(`[PIN 검증] 🔍 현재 pinVerificationState: ${pinVerificationState} → ${state}`);
        setPinVerificationState(state);
        devLog(`[PIN 검증] 🔍 상태 변경 완료: ${state}`);

        // 상태에 따른 로딩 상태 관리
        switch (state) {
          case 'verifying':
          case 'server_verifying':
            devLog(`[PIN 검증] 🔄 로딩 상태: false → true (${state})`);
            setIsLoading(true);
            break;
          case 'failed':
          case 'ready_for_retry':
          case 'reset': // 🔥 reset 상태 추가 (네이티브와 통일)
          case 'idle':
            devLog(`[PIN 검증] 🔄 로딩 상태: true → false (${state})`);
            setIsLoading(false);
            break;
        }
      }
    };

    try {
      devLog("[PIN 검증] 📱 네이티브 PIN 다이얼로그 호출 중...");
      // PIN 검증 실행 - AuthenticationFlow용 모드 지정
      await pinVerificationManager.executeVerification(formData, callbacks, 'auth');
      devLog("[PIN 검증] 📱 네이티브 PIN 다이얼로그 호출 완료");
    } catch (error) {
      devError("[PIN 검증] 💥 전체 프로세스 오류:", error);
      devLog("[PIN 검증] 💥 오류 상세:", {
        message: error.message,
        stack: error.stack
      });
      callbacks.onError(error);
    } finally {
      devLog("[PIN 검증] 🏁 handlePinVerification 종료");
      setIsLoading(false);
      devLog("==============================================================");
    }
  };

  // 로그인 처리 함수 추가
  const handleLogin = () => {
    devLog("==============================================================");
    devLog("[로그인 처리] 시작");
    devLog(`[로그인 처리] 사용자 전화번호: ${formData.phoneNumber}`);

    let signInData = {
      storeId: "root",
      userName: formData.phoneNumber,
      role: "MEMBER"
    };

    devLog("[로그인 처리] API 요청 데이터:", signInData);

    requester.restoreAccount(signInData, (result) => {
      if (result.code === 0) {
        requester.userNumberSignIn(signInData, (signInResult) => {
          devLog("[로그인 처리] API 응답:", signInResult);

          if (signInResult.code === 0) {
            const tokenData = signInResult.data.token;
            devLog("[로그인 처리] 로그인 성공 - 토큰 발급됨");

            // 사용자 정보 조회 (있다면)
            if (signInResult.data.member) {
              devLog("[로그인 처리] 회원 정보:", {
                memberId: signInResult.data.member.memberId,
                name: signInResult.data.member.name,
                mobileNumber: signInResult.data.member.mobileNumber,
                hasBiometric: !!signInResult.data.member.biometricEnabled
              });
            }

            devLog("[로그인 처리] Redux 상태 업데이트 - 토큰 저장");
            dispatch(AuthReducer.actions.setToken(tokenData));

            // 네이티브에 토큰 전달
            if (window.webkit?.messageHandlers?.BRIDGE) {
              devLog("[로그인 처리] iOS 네이티브에 토큰 전달");
              window.webkit.messageHandlers.BRIDGE.postMessage({
                command: "setToken",
                token: tokenData
              });
            } else if (window.BRIDGE?.setToken) {
              devLog("[로그인 처리] 안드로이드 네이티브에 토큰 전달");
              try {
                window.BRIDGE.setToken(JSON.stringify(tokenData));
              } catch (e) {
                devError("[로그인 처리] 네이티브 토큰 전달 오류:", e);
              }
            }

            devLog(`[로그인 처리] 메인 페이지로 이동: /ws/${props.storeCode}/mobile/main`);
            // 바로 메인 페이지로 이동
            navigate(`/ws/${props.storeCode}/mobile/main`, { replace: true });
          } else {
            devError("[로그인 처리] 로그인 실패:", signInResult);
            toast.error(t("로그인 중 오류가 발생했습니다."));
          }

          devLog("[로그인 처리] 완료");
          devLog("==============================================================");
        });
      }
    });
  };

  // 🔥 재인증 시 생체인증 등록
  const registerBiometricForReauth = async () => {
    if (!biometricStatus.supported) {
      toast.error(t("이 기기는 생체인증을 지원하지 않습니다"));
      return;
    }

    setIsLoading(true);

    try {
      devLog("[registerBiometricForReauth] 재인증 생체인증 등록 시작");

      // 최대 시도 횟수와 현재 시도 횟수 설정
      const maxAttempts = 5;
      let currentAttempt = 0;
      let registrationSuccess = false;

      const attemptBiometricRegistration = async () => {
        try {
          currentAttempt++;
          devLog("[registerBiometricForReauth] 생체인증 등록 시도", { currentAttempt, maxAttempts });

          // 사용자 ID로 휴대폰 번호 사용
          const userId = formData.phoneNumber;

          // 생체인증 키 등록 진행
          const biometricKeyResult = await biometricService.registerBiometricKey(userId);
          devLog("[registerBiometricForReauth] 생체인증 키 등록 결과", { biometricKeyResult });

          if (biometricKeyResult && biometricKeyResult.publicKey) {
            // 🔥 HamburgerMenu 방식과 동일하게 서버에 생체인증 정보 업데이트
            const updateData = {
              userName: formData.phoneNumber,
              biometricPublicKey: biometricKeyResult.publicKey,
              biometricAlgorithm: biometricKeyResult.algorithm || "EC",
              biometricEnabled: true,
              biometricKeySignature: calculateKeySignature(biometricKeyResult.publicKey),
              biometricPlatform: biometricService.getPlatform()
            };

            devLog("[registerBiometricForReauth] 서버 업데이트 요청 데이터", updateData);

            // 🔥 HamburgerMenu와 동일한 API 사용
            requester.checkMemberBiometricStatus(updateData, (result) => {
              devLog("[registerBiometricForReauth] 서버 업데이트 응답", result);

              if (result.code === 0) {
                registrationSuccess = true;
                devLog("[registerBiometricForReauth] 생체인증 업데이트 성공");

                // 로컬 스토리지에 상태 저장
                localStorage.setItem('puffu_biometric_supported', 'true');

                toast.success(t("생체인증이 성공적으로 등록되었습니다!"), {
                  autoClose: 2000,
                  position: toast.POSITION.BOTTOM_CENTER
                });

                // 로그인 진행
                setIsLoading(false);
                performLogin();
              } else {
                devError("[registerBiometricForReauth] 서버 업데이트 실패", result);
                throw new Error("서버 업데이트 실패");
              }
            });
          } else {
            devError("[registerBiometricForReauth] 생체인증 키 생성 실패");
            throw new Error("생체인증 키 생성 실패");
          }
        } catch (error) {
          devError("[registerBiometricForReauth] 생체인증 등록 시도 오류:", error);

          if (currentAttempt < maxAttempts) {
            toast.warning(t("생체인증 등록에 실패했습니다. 다시 시도합니다.") + ` (${currentAttempt}/${maxAttempts})`, {
              autoClose: 2000,
              position: toast.POSITION.BOTTOM_CENTER
            });
            setTimeout(() => {
              attemptBiometricRegistration();
            }, 1500);
          } else {
            devError("[registerBiometricForReauth] 최대 시도 횟수 초과");
            toast.error(t("생체인증 등록에 여러 번 실패했습니다. 로그인을 진행합니다."), {
              autoClose: 3000,
              position: toast.POSITION.BOTTOM_CENTER
            });
            // 생체인증 없이 로그인 진행
            setIsLoading(false);
            performLogin();
          }
        }
      };

      // 첫 번째 시도 시작
      await attemptBiometricRegistration();
    } catch (error) {
      devError("[registerBiometricForReauth] 최종 오류:", error);
      toast.error(t("생체인증 등록 중 오류가 발생했습니다. 로그인을 진행합니다."));
      setIsLoading(false);
      performLogin();
    }
  };

  // 단계에 따른 렌더링
  const renderCurrentStep = () => {
    switch (currentStep) {
      case STEPS.PHONE_NUMBER:
        return renderPhoneNumberStep();
      case STEPS.AUTHENTICATION_METHOD:  // 🔥 인증 방식 선택
        return renderAuthenticationMethodStep();
      case STEPS.RESIDENT_NUMBER:
        return renderResidentNumberStep();
      case STEPS.MOBILE_CARRIER:
        return renderMobileCarrierStep();
      case STEPS.NAME:
        return renderNameStep();
      case STEPS.TERMS:
        return renderTermsStep();
      case STEPS.SMS_VERIFICATION:
        return renderSmsVerificationStep();
      case STEPS.PASS_VERIFICATION:      // 🔥 PASS 인증
        return renderPassVerificationStep();
      case STEPS.PIN_REGISTRATION:
        return renderPinRegistrationStep();
      case STEPS.BIOMETRIC_OPTION:
        return renderBiometricOptionStep();
      case STEPS.PIN_VERIFICATION:
        return renderPinVerificationStep();
      default:
        return renderPhoneNumberStep();
    }
  };

  // 단계별 진행 상태 표시
  const renderProgressBar = () => {
    const totalSteps = Object.keys(STEPS).length;
    const progress = ((currentStep + 1) / totalSteps) * 100;

    return (
      <div className={styles.progressContainer}>
        <div
          className={styles.progressBar}
          style={{ width: `${progress}%` }}
        ></div>
      </div>
    );
  };

  // 다음 단계로 이동 처리하는 함수
  const handleNextStep = () => {
    devLog("[handleNextStep] 시작", { currentStep, formData });

    // 키보드 숨기기
    hideNativeKeyboard();

    switch (currentStep) {
      case STEPS.PHONE_NUMBER:
        // 휴대폰 번호 유효성 검사
        if (inputs.current[0] && inputs.current[0].isValid && inputs.current[0].isValid()) {
          devLog("[handleNextStep] 휴대폰 번호 유효성 검사 통과, 회원 확인 시작");
          checkExistingMemberAndRoute(); // 🔥 즉시 회원 확인 후 라우팅
        } else {
          devWarn("[handleNextStep] 휴대폰 번호 유효성 검사 실패");
          toast.error(t("올바른 휴대폰 번호를 입력해주세요"));
        }
        break;

      case STEPS.AUTHENTICATION_METHOD:
        // 🔥 신규 회원의 인증 방식 선택에 따른 라우팅 (이미 신규 회원임이 확인된 상태)
        if (formData.authenticationMethod === 'PASS') {
          devLog("[handleNextStep] PASS 인증 선택 - 통신사 선택으로 이동");
          moveToNextStep(STEPS.MOBILE_CARRIER); // 🔥 PASS도 통신사 정보 필요
        } else if (formData.authenticationMethod === 'SMS') {
          devLog("[handleNextStep] SMS 인증 선택 - 주민번호 입력으로 이동");
          moveToNextStep(STEPS.RESIDENT_NUMBER); // 기존 SMS 플로우
        } else {
          toast.error(t("인증 방식을 선택해주세요"));
        }
        break;

      case STEPS.RESIDENT_NUMBER:
        // 주민번호 유효성 검사 (앞 6자리와 뒷 첫번째 자리만 확인)
        devLog("[handleNextStep] 주민번호 확인", {
          front: formData.residentNumberFront.length,
          back: formData.residentNumberBack.length
        });

        if (formData.residentNumberFront.length === 6 && formData.residentNumberBack.length >= 1) {
          devLog("[handleNextStep] 주민번호 형식 확인 완료, 통신사 선택 단계로 이동");
          moveToNextStep(STEPS.MOBILE_CARRIER);
        } else {
          devWarn("[handleNextStep] 주민번호 형식 오류");
          toast.error(t("올바른 주민등록번호를 입력해주세요"));
        }
        break;

      case STEPS.MOBILE_CARRIER:
        // 통신사 선택 확인
        devLog("[handleNextStep] 통신사 선택 확인", { 
          carrierCode: formData.carrierCode,
          authenticationMethod: formData.authenticationMethod 
        });

        if (formData.carrierCode) {
          // 통신사 정보를 즉시 완료된 단계로 표시
          const carrierInfo = {
            id: STEPS.MOBILE_CARRIER,
            title: t("통신사"),
            value: CARRIERS.find(c => c.id === formData.carrierCode)?.name || formData.carrierCode
          };

          // 즉시 완료된 단계 정보 업데이트
          setCompletedSteps(prev => ({
            ...prev,
            [STEPS.MOBILE_CARRIER]: carrierInfo
          }));

          // 🔥 인증 방식에 따른 분기 처리
          if (formData.authenticationMethod === 'PASS') {
            devLog("[handleNextStep] PASS 인증 - 통신사 선택 완료 → 이름 입력으로 이동");
            setCurrentStep(STEPS.NAME); // PASS는 주민번호 생략하고 바로 이름으로
          } else if (formData.authenticationMethod === 'SMS') {
            devLog("[handleNextStep] SMS 인증 - 통신사 선택 완료 → 이름 입력으로 이동");
            setCurrentStep(STEPS.NAME); // SMS도 이름 입력으로 (주민번호는 이미 입력 완료)
          } else {
            // 기본값 (하위 호환성)
            devLog("[handleNextStep] 인증방식 미설정 - 기본 이름 입력으로 이동");
            setCurrentStep(STEPS.NAME);
          }
          
          setIsEditMode(false);
        } else {
          devWarn("[handleNextStep] 통신사 미선택");
          toast.error(t("통신사를 선택해주세요"));
        }
        break;

      case STEPS.NAME:
        // 이름 입력 확인
        devLog("[handleNextStep] 이름 입력 확인");

        if (inputs.current[3] && inputs.current[3].getValue && inputs.current[3].getValue().trim().length > 0) {
          devLog("[handleNextStep] 이름 입력 완료, 약관 동의 단계로 이동");
          moveToNextStep(STEPS.TERMS);
        } else {
          devWarn("[handleNextStep] 이름 미입력");
          toast.error(t("이름을 입력해주세요"));
        }
        break;

      case STEPS.TERMS:
        // 필수 약관 동의 확인
        devLog("[handleNextStep] 약관 동의 확인", {
          termsAgreed: formData.termsAgreed,
          privacyAgreed: formData.privacyAgreed,
          authenticationMethod: formData.authenticationMethod
        });

        if (formData.termsAgreed && formData.privacyAgreed) {
          // 🔥 인증 방식에 따른 조건부 처리
          if (formData.authenticationMethod === 'SMS') {
            devLog("[handleNextStep] SMS 인증 방식 - SMS 인증 요청 시작");
            requestSmsVerification();
          } else if (formData.authenticationMethod === 'PASS') {
            devLog("[handleNextStep] PASS 인증 방식 - PASS 인증 단계로 이동");
            requestPassVerification(); // 🔥 새로 구현할 함수
          } else {
            toast.error(t("인증 방식이 선택되지 않았습니다"));
          }
        } else {
          devWarn("[handleNextStep] 필수 약관 미동의");
          toast.error(t("필수 약관에 동의해주세요"));
        }
        break;

      case STEPS.SMS_VERIFICATION:
        // SMS 인증번호 확인
        devLog("[handleNextStep] SMS 인증번호 확인 시작");
        verifySmsCode(formData.smsCode);
        break;

      case STEPS.PASS_VERIFICATION:
        // 🔥 PASS 인증 확인
        devLog("[handleNextStep] PASS 인증 확인 시작");
        verifyPassAuthentication();
        break;

      case STEPS.PIN_REGISTRATION:
        // PIN 번호 등록
        devLog("[handleNextStep] PIN 번호 등록 시작");
        registerPinNumber();
        break;

      case STEPS.BIOMETRIC_OPTION:
        // 생체인증 등록 선택 (완료 처리)
        devLog("[handleNextStep] 생체인증 등록 옵션 처리, 완료 단계로 이동");
        completeRegistration();
        break;

      case STEPS.PIN_VERIFICATION:
        // PIN 검증
        handlePinVerification();
        break;
    }
  };

  // 🔥 PASS 인증 요청
  const requestPassVerification = () => {
    devLog("[requestPassVerification] PASS 인증 요청 시작");
    setIsLoading(true);

    // API 요청을 위한 토큰 요청 (NumberVerificationModal 로직 참조)
    bffRequester.getToken((tokenResult) => {
      devLog("[requestPassVerification] 토큰 응답 받음", { tokenResult });

      let tokenData = tokenResult;
      let token = {
        "encryptMOKToken": tokenData.encryptMOKToken,
        "publicKey": tokenData.publicKey
      };
      let jsonData = JSON.stringify(token);

      // PASS 인증 요청 데이터 구성 (통신사 정보 포함)
      let data = {
        "serviceType": "telcoAuth", 
        "providerid": formData.carrierCode || "PASS", // 🔥 실제 통신사 코드 사용
        "reqAuthType": "PASS",
        "usageCode": "01005",
        "userName": formData.name,
        "userPhoneNum": formData.phoneNumber.replace(/-/g, ''),
        "retTransferType": "MOKResult",
        "normalFlag": "Y",
        "MOKAuthRequestData": jsonData
      };

      devLog("[requestPassVerification] PASS 인증 요청 데이터", { data });

      // PASS 인증 요청 API 호출
      bffRequester.apiRequest(data, (result) => {
        devLog("[requestPassVerification] PASS 인증 응답", { result });
        setIsLoading(false);

        try {
          const outerMOKConfirmData = result.MOKConfirmData;
          const innerMOKConfirmData = JSON.parse(outerMOKConfirmData);

          if (innerMOKConfirmData.resultCode === "2000") {
            // PASS 인증 요청 성공
            devLog("[requestPassVerification] PASS 인증 요청 성공");

            const logData = {
              "storeCode": "5",
              "resultCode": innerMOKConfirmData.resultCode,
              "authType": "PASS",
              "purpose": "login"
            }

            requester.createAdultVerificationLog(logData, (res) => {
              if (res.code === 0) {
                devLog("[requestPassVerification] PASS 인증 로그 저장 성공");
              } else {
                devError("[requestPassVerification] PASS 인증 로그 저장 실패", { res });
              }
            });
            
            // 응답 데이터 저장
            setAuthData({
              tokenData,
              MOKConfirmData: innerMOKConfirmData,
              requestData: data
            });

            // PASS 인증 단계로 이동
            moveToNextStep(STEPS.PASS_VERIFICATION);
            toast.info(t("PASS 앱에서 본인인증을 진행해주세요"));
          } else {
            devError("[requestPassVerification] PASS 인증 요청 실패", {
              resultCode: innerMOKConfirmData.resultCode,
              resultMsg: innerMOKConfirmData.resultMsg
            });
            toast.error(innerMOKConfirmData.resultMsg || t("PASS 인증 요청에 실패했습니다."));
          }
        } catch (error) {
          devError("[requestPassVerification] PASS 인증 요청 파싱 오류:", error);
          toast.error(t("PASS 인증 요청 중 오류가 발생했습니다."));
        }
      });
    });
  };

  // SMS 인증번호 요청
  const requestSmsVerification = () => {
    devLog("[requestSmsVerification] 시작", {
      formData: {
        phoneNumber: formData.phoneNumber,
        name: formData.name,
        carrierCode: formData.carrierCode,
        residentNumberFront: formData.residentNumberFront,
        residentNumberBack: formData.residentNumberBack?.charAt(0) || ''
      }
    });

    setIsLoading(true);

    // API 요청을 위한 토큰 요청
    devLog("[requestSmsVerification] 토큰 요청 시작");
    bffRequester.getToken((tokenResult) => {
      devLog("[requestSmsVerification] 토큰 응답 받음", { tokenResult });

      let tokenData = tokenResult;
      let token = {
        "encryptMOKToken": tokenData.encryptMOKToken,
        "publicKey": tokenData.publicKey
      };
      let jsonData = JSON.stringify(token);

      devLog("[requestSmsVerification] 토큰 데이터 준비", { token });

      // 생년월일 및 성별 추출
      // 주민번호 앞 6자리를 8자리 생년월일로 변환 - 컴포넌트 외부 함수 사용
      const birthDay = getBirthDateFromResidentNumber(formData.residentNumberFront, formData.residentNumberBack);
      const gender = formData.residentNumberBack ? (parseInt(formData.residentNumberBack.charAt(0)) % 2 === 1 ? "1" : "2") : "";
      const nation = formData.residentNumberBack ? "0" : "";

      devLog("[requestSmsVerification] 사용자 정보 추출", { birthDay, gender, nation });

      // 앱 서명 해시 가져오기 (네이티브에서 제공하는 경우)
      let appSignatureHash = '';
      if (window.BRIDGE && typeof window.BRIDGE.getAppSignatureHash === 'function') {
        try {
          devLog("[requestSmsVerification] 앱 서명 해시 함수 호출 시도");
          appSignatureHash = window.BRIDGE.getAppSignatureHash();
          devLog("[requestSmsVerification] 앱 서명 해시:", appSignatureHash);
        } catch (error) {
          devError("[requestSmsVerification] 앱 서명 해시 가져오기 실패:", error);
        }
      } else {
        devWarn("[requestSmsVerification] getAppSignatureHash 함수를 사용할 수 없음. window.BRIDGE 상태:", !!window.BRIDGE);
      }

      // SMS 메시지 형식 설정 (토스 패턴 참고)
      let smsMessage = appSignatureHash
        ? `푸푸 인증번호 [000000]를 입력해주세요`
        : "푸푸 인증번호 [000000]를 입력해주세요:";

      devLog("[requestSmsVerification] 해시가 포함된 SMS 메시지:", smsMessage);

      // SMS 인증 요청 데이터 구성
      let data = {
        "serviceType": "telcoAuth",
        "providerid": formData.carrierCode, // 통신사 코드
        "reqAuthType": "SMS",
        "usageCode": "01005",
        "userName": formData.name,
        "userPhoneNum": formData.phoneNumber.replace(/-/g, ''),
        "userBirthday": birthDay,
        "userGender": gender,
        "sendMsg": smsMessage,
        "replyNumber": "01048947486", // 회신 번호
        "retTransferType": "MOKResult",
        "userNation": nation,
        "normalFlag": "Y",
        "MOKAuthRequestData": jsonData
      };

      devLog("[requestSmsVerification] API 요청 데이터 준비", { data });

      // SMS 인증 요청 API 호출
      devLog("[requestSmsVerification] API 요청 시작");
      bffRequester.apiRequest(data, (result) => {
        devLog("[requestSmsVerification] API 응답 받음", { result });
        setIsLoading(false);

        // 응답 파싱
        try {
          devLog("[requestSmsVerification] 응답 파싱 시작");
          const outerMOKConfirmData = result.MOKConfirmData;
          devLog("[requestSmsVerification] outerMOKConfirmData", { outerMOKConfirmData });

          const innerMOKConfirmData = JSON.parse(outerMOKConfirmData);
          devLog("[requestSmsVerification] innerMOKConfirmData 파싱 완료", {
            resultCode: innerMOKConfirmData.resultCode,
            resultMsg: innerMOKConfirmData.resultMsg
          });

          if (innerMOKConfirmData.resultCode === "2000") {
            // 인증번호 요청 성공
            devLog("[requestSmsVerification] 인증번호 요청 성공");
            const logData = {
              "storeCode": "5",
              "resultCode": innerMOKConfirmData.resultCode,
              "authType": "SMS",
              "purpose": "login"
            }

            requester.createAdultVerificationLog(logData, (res) => {
              if (res.code === 0) {
                devLog("[requestSmsVerification] SMS 인증 로그 저장 성공");
              } else {
                devError("[requestSmsVerification] SMS 인증 로그 저장 실패", { res });
              }
            });

            // 응답 데이터 세션 저장
            setAuthData({
              tokenData,
              MOKConfirmData: innerMOKConfirmData,
              requestData: data
            });
            devLog("[requestSmsVerification] 인증 데이터 저장 완료");

            // 안드로이드 SMS 인증번호 자동 수신 시도
            if (biometricStatus.platform === 'android' && window.BRIDGE?.requestSmsRetrieval) {
              devLog("[requestSmsVerification] 안드로이드 SMS 자동 수신 시도");
              try {
                window.BRIDGE.requestSmsRetrieval();
              } catch (error) {
                devError("[requestSmsVerification] SMS 자동 수신 재요청 오류:", error);
              }
            }

            // 타이머 재설정 및 활성화
            setTimeRemaining(180);
            setIsTimerActive(true);
            devLog("[requestSmsVerification] 타이머 설정 완료", { timeRemaining: 180 });

            // 다음 단계로 이동
            moveToNextStep(STEPS.SMS_VERIFICATION);
            devLog("[requestSmsVerification] 다음 단계로 이동 완료", { nextStep: STEPS.SMS_VERIFICATION });

            toast.info(t("인증번호가 발송되었습니다"));
          } else {
            // 인증번호 요청 실패
            devError("[requestSmsVerification] 인증번호 요청 실패", {
              resultCode: innerMOKConfirmData.resultCode,
              resultMsg: innerMOKConfirmData.resultMsg
            });
            toast.error(innerMOKConfirmData.resultMsg || t("인증번호 요청에 실패했습니다."));
          }
        } catch (error) {
          devError("[requestSmsVerification] SMS 인증 요청 파싱 오류:", error);
          devError("[requestSmsVerification] 원본 응답:", result);
          toast.error(t("인증번호 요청 중 오류가 발생했습니다. 다시 시도해주세요."));
        }
      });
    });

    devLog("[requestSmsVerification] 함수 실행 완료 (비동기 작업 진행 중)");
  };

  // PIN 검증 단계에 대한 코멘트
  // PIN 검증 단계 진입 시 자동으로 다이얼로그 호출
  useEffect(() => {
    devLog("==============================================================");
    devLog("[PIN 검증 useEffect] 발동됨, 현재 단계:", currentStep, "PIN_VERIFICATION 단계:", STEPS.PIN_VERIFICATION);
    devLog("[PIN 검증 useEffect] isVerifyingRef.current:", isVerifyingRef.current);
    devLog("[PIN 검증 useEffect] 로그인 상태 체크:", { userId, userName }); // 🔥 로그인 상태 로그 추가

    // 🔥 이미 로그인된 사용자는 자동 인증을 실행하지 않음
    if (userId || userName) {
      devLog("[PIN 검증 useEffect] 이미 로그인된 사용자 - 자동 인증 건너뛰기");
      return;
    }

    if (currentStep === STEPS.PIN_VERIFICATION && !isVerifyingRef.current) {
      devLog("[PIN 검증 useEffect] PIN 검증 단계 진입 확인됨");

      // PIN 검증 재시작 핸들러 등록 (추가)
      window.restartPinVerification = () => {
        devLog("[PIN 검증] 재시작 요청 받음");
        if (currentStep === STEPS.PIN_VERIFICATION && !isVerifyingRef.current) {
          devLog("[PIN 검증] 재시작 실행");
          handlePinVerification();
        }
      };

      // 웹뷰 PIN 상태 업데이트 핸들러 등록 (네이티브에서 호출)
      window.updatePinVerificationState = (state, message = "") => {
        devLog(`[웹뷰 상태 업데이트] 네이티브에서 상태 변경 요청: ${state} - ${message}`);

        // 상태 업데이트
        setPinVerificationState(state);

        // 상태에 따른 로딩 상태 관리
        switch (state) {
          case 'verifying':
          case 'server_verifying':
            setIsLoading(true);
            break;
          case 'failed':
          case 'ready_for_retry':
          case 'reset': // 🔥 reset 상태 추가 (네이티브와 통일)
            setIsLoading(false);
            break;
          case 'idle':
            setIsLoading(false);
            break;
        }

        devLog(`[웹뷰 상태 업데이트] 상태 변경 완료: ${state}`);
      };

      // 디바이스 저장 정보 확인 후 PIN 검증 시도
      const checkAndVerify = async () => {
        try {
          // 🔥 네이티브 명령어 실패에 대한 안전한 처리
          let hasSavedPin = false;
          try {
            hasSavedPin = await biometricService.hasPin();
          } catch (error) {
            devWarn("[PIN 검증 useEffect] hasPin() 실패 (네이티브 명령어 미지원), 무시하고 진행:", error);
            hasSavedPin = false; // 실패 시 false로 처리
          }
          
          const savedPhoneNumber = biometricService.getPhoneNumber();
          const hasSavedInfo = hasSavedPin && savedPhoneNumber;

          devLog("[PIN 검증 useEffect] 디바이스 저장 정보 확인:", {
            hasSavedPin,
            savedPhoneNumber,
            hasSavedInfo,
            isExistingMember
          });

          // 🔥 로그아웃 후 상황 개선: 기존 회원이거나 저장된 전화번호가 있으면 PIN 검증 시작
          if (hasSavedInfo) {
            devLog("[PIN 검증 useEffect] 디바이스에 완전한 정보 있음, PIN 검증 시작");
            handlePinVerification();
          } else if (isExistingMember || savedPhoneNumber) {
            // 🔥 기존 회원이거나 전화번호가 있으면 PIN 상태와 관계없이 검증 시작
            devLog("[PIN 검증 useEffect] 기존 회원 또는 저장된 전화번호 확인 - PIN 검증 시작");
            devLog("[PIN 검증 useEffect] 로그아웃 후 또는 네이티브 명령어 실패 상황으로 판단하여 서버 PIN 검증 진행");
            handlePinVerification();
          } else {
            devLog("[PIN 검증 useEffect] 신규 사용자로 판단, PIN 입력 대기");
            isVerifyingRef.current = false;
          }
        } catch (error) {
          devError("[PIN 검증 useEffect] 디바이스 정보 확인 오류:", error);
          // 🔥 오류 발생 시에도 기존 회원이면 PIN 검증 시도
          if (isExistingMember) {
            devLog("[PIN 검증 useEffect] 오류 발생했지만 기존 회원이므로 PIN 검증 시도");
            handlePinVerification();
          } else {
            isVerifyingRef.current = false;
          }
        }
      };

      // 약간의 지연 후 실행 (상태 업데이트 등이 모두 완료된 후)
      setTimeout(() => {
        checkAndVerify();
      }, 300);
    }

    // 컴포넌트 언마운트 시 핸들러 정리 (추가)
    return () => {
      if (window.restartPinVerification) {
        delete window.restartPinVerification;
      }
      if (window.updatePinVerificationState) {
        delete window.updatePinVerificationState;
      }
      if (window.notifyPinVerificationFailedAndRestart) {
        delete window.notifyPinVerificationFailedAndRestart;
      }
    };
  }, [currentStep, isExistingMember, userId, userName]); // 🔥 의존성 배열에 userId, userName 추가

  // 🔥 새로운 useEffect 추가: ready_for_retry/reset 상태 감지하여 자동 재시작
  useEffect(() => {
    if ((pinVerificationState === 'ready_for_retry' || pinVerificationState === 'reset') && currentStep === STEPS.PIN_VERIFICATION) {
      devLog("[PIN 검증 자동 재시작] ready_for_retry/reset 상태 감지, 자동 재시작 준비");

      // 약간의 지연 후 자동으로 새로운 검증 프로세스 시작 (시간 더 단축)
      const retryTimeout = setTimeout(() => {
        devLog("[PIN 검증 자동 재시작] 새로운 검증 프로세스 시작");
        handlePinVerification();
      }, 30); // 🔥 100ms → 30ms로 단축 (속도 개선)

      // 클린업 함수로 타임아웃 정리
      return () => {
        clearTimeout(retryTimeout);
      };
    }
  }, [pinVerificationState, currentStep]);

  // 🔥 인증방식 선택 시 자동 진행 제거 - 사용자가 직접 "다음" 버튼 클릭 시에만 진행

  // 🔥 재인증 시 PIN 업데이트 처리
  const handleReauthenticationPinUpdate = (biometricData = null) => {
    devLog("[handleReauthenticationPinUpdate] 재인증 PIN 업데이트 시작");
    const platform = biometricService.getPlatform(); // 🔥 추가
    // PIN 해시 값 추출
    let pinHashValue = "";
    try {
      if (typeof formData.pinHash === 'string') {
        if (platform === 'android') {
          // Android: JSON 파싱 시도
          const parsedPin = JSON.parse(formData.pinHash);
          pinHashValue = parsedPin.pinHash || parsedPin.hashedPin || formData.pinHash;
        } else {
          // iOS: 바로 사용
          pinHashValue = formData.pinHash;
        }
      } else if (formData.pinHash && typeof formData.pinHash === 'object') {
        pinHashValue = formData.pinHash.pinHash || formData.pinHash.hashedPin;
      }
    } catch (error) {
      devError("[handleReauthenticationPinUpdate] PIN 해시 처리 오류:", error);
    }

    // PIN 업데이트 API 호출
    const pinUpdateData = {
      userName: formData.phoneNumber,
      pinHash: pinHashValue
    };

    devLog("[handleReauthenticationPinUpdate] PIN 업데이트 요청:", pinUpdateData);

    requester.registerPinByUserName(pinUpdateData, (result) => {
      devLog("[handleReauthenticationPinUpdate] PIN 업데이트 응답:", result);

      if (result.code === 0) {
        devLog("[handleReauthenticationPinUpdate] PIN 업데이트 성공");

        // 생체인증 데이터가 있으면 생체인증도 업데이트
        if (biometricData) {
          devLog("[handleReauthenticationPinUpdate] 생체인증 데이터 업데이트 진행");

          // 🔥 서버에 생체인증 정보 업데이트 (HamburgerMenu 방식과 동일)
          const updateData = {
            userName: formData.phoneNumber,
            biometricPublicKey: biometricData.biometricPublicKey,
            biometricAlgorithm: biometricData.biometricAlgorithm,
            biometricEnabled: true,
            biometricKeySignature: biometricData.biometricKeySignature,
            biometricPlatform: biometricData.biometricPlatform
          };

          devLog("[handleReauthenticationPinUpdate] 서버 생체인증 업데이트 요청 데이터", updateData);

          // checkMemberBiometricStatus API 호출
          requester.checkMemberBiometricStatus(updateData, (result) => {
            devLog("[handleReauthenticationPinUpdate] 서버 생체인증 업데이트 응답", result);

            if (result.code === 0) {
              devLog("[handleReauthenticationPinUpdate] 생체인증 업데이트 성공");

              // 로컬 스토리지에 상태 저장
              localStorage.setItem('puffu_biometric_supported', 'true');

              // 로그인 진행
              performLogin();
            } else {
              devError("[handleReauthenticationPinUpdate] 생체인증 업데이트 실패", result);
              // 실패해도 로그인은 진행
              performLogin();
            }
          });
        } else {
          // 생체인증 옵션 제공
          devLog("[handleReauthenticationPinUpdate] 생체인증 옵션 제공");
          setIsReauthenticationComplete(true);
          setCurrentStep(STEPS.BIOMETRIC_OPTION);
        }
      } else {
        setIsLoading(false);
        devError("[handleReauthenticationPinUpdate] PIN 업데이트 실패:", result);
        toast.error(t("PIN 업데이트 중 오류가 발생했습니다"));
      }
    });
  };

  // 🔥 탈퇴한 회원의 restore 처리 함수
  const handleWithdrawnMemberRestore = (biometricData) => {
    devLog("[handleWithdrawnMemberRestore] 탈퇴한 회원 restore 시작");

    const platform = biometricService.getPlatform();
    // PIN 해시 값 추출
    let pinHashValue = "";
    try {
      if (typeof formData.pinHash === 'string') {
        if (platform === 'android') {
          // Android: JSON 파싱 시도
          const parsedPin = JSON.parse(formData.pinHash);
          pinHashValue = parsedPin.pinHash || parsedPin.hashedPin || formData.pinHash;
        } else {
          // iOS: 바로 사용
          pinHashValue = formData.pinHash;
        }
      } else if (formData.pinHash && typeof formData.pinHash === 'object') {
        pinHashValue = formData.pinHash.pinHash || formData.pinHash.hashedPin;
      }
    } catch (error) {
      devError("[handleWithdrawnMemberRestore] PIN 해시 처리 오류:", error);
    }

    // restore용 데이터 구성
    let restoreData = {
      storeId: "root",
      userName: formData.phoneNumber,
      role: "MEMBER",
      pinHash: pinHashValue
    };

    // 생체인증 데이터가 있으면 추가
    if (biometricData) {
      restoreData = {
        ...restoreData,
        biometricPublicKey: biometricData.biometricPublicKey,
        biometricAlgorithm: biometricData.biometricAlgorithm,
        biometricEnabled: true,
        biometricKeySignature: biometricData.biometricKeySignature,
        biometricPlatform: biometricData.biometricPlatform
      };
    }

    devLog("[handleWithdrawnMemberRestore] restore 요청 데이터:", restoreData);

    // 기존 restore 로직 사용
    requester.restoreAccount(restoreData, (result) => {
      if (result.code === 0) {
        devLog("[handleWithdrawnMemberRestore] restore 성공, 로그인 진행");
        performLoginAfterRestore();
      } else {
        setIsLoading(false);
        devError("[handleWithdrawnMemberRestore] restore 실패:", result);
        toast.error(t("계정 복구 중 오류가 발생했습니다"));
      }
    });
  };

  // 🔥 공통 로그인 함수 (신규회원 vs 기존회원 구분)
  const performLogin = () => {
    devLog("[performLogin] 로그인 시작");
    
    // 🔥 신규회원인지 기존회원인지 판별
    const isNewMemberSignup = (
      !isReauthentication && 
      !isReauthenticationComplete && 
      authData.verificationResult && // SMS/PASS 인증 완료한 신규회원
      formData.name && 
      formData.residentNumber
    );

    devLog("[performLogin] 회원 유형 판별:", {
      isNewMemberSignup,
      isReauthentication,
      isReauthenticationComplete,
      hasAuthData: !!authData.verificationResult,
      hasName: !!formData.name,
      hasResidentNumber: !!formData.residentNumber
    });

    if (isNewMemberSignup) {
      // 🔥 신규회원: restoreAccount 없이 바로 로그인
      devLog("[performLogin] 신규회원 로그인 - restoreAccount 건너뛰기");
      performLoginAfterRestore();
    } else {
      // 🔥 기존회원: 기존 로직 (restoreAccount 후 로그인)
      devLog("[performLogin] 기존회원 로그인 - restoreAccount 진행");

    let signInData = {
      storeId: "root",
      userName: formData.phoneNumber,
      role: "MEMBER"
    };

    requester.restoreAccount(signInData, (result) => {
      if (result.code === 0) {
        performLoginAfterRestore();
        } else {
          setIsLoading(false);
          devError("[performLogin] restoreAccount 실패:", result);
          toast.error(t("로그인 중 오류가 발생했습니다"));
      }
    });
    }
  };

  // 🔥 restore 후 로그인 처리 공통 함수
  const performLoginAfterRestore = () => {
    let signInData = {
      storeId: "root",
      userName: formData.phoneNumber,
      role: "MEMBER"
    };

    requester.userNumberSignIn(signInData, (signInResult) => {
      setIsLoading(false);

      if (signInResult.code === 0) {
        // 로그인 성공
        devLog("[performLoginAfterRestore] 로그인 성공");
        const tokenData = signInResult.data.token;
        dispatch(AuthReducer.actions.setToken(tokenData));

        // 연락처 저장 - BiometricService 활용
        const phoneNumberSaved = biometricService.savePhoneNumber(formData.phoneNumber);
        devLog("[performLoginAfterRestore] 휴대폰 번호 저장 결과:", phoneNumberSaved);

        // PIN 해시가 이미 네이티브에 저장되어 있음을 확인
        const hasPinStatus = biometricService.hasPin();
        devLog("[performLoginAfterRestore] PIN 상태 확인:", hasPinStatus);

        // 네이티브에 토큰 전달
        if (window.webkit?.messageHandlers?.BRIDGE) {
          window.webkit.messageHandlers.BRIDGE.postMessage({
            command: "setToken",
            token: tokenData
          });
          devLog("[performLoginAfterRestore] iOS 네이티브에 토큰 전달 완료");
        } else if (window.BRIDGE?.setToken) {
          // 안드로이드 토큰 설정
          try {
            window.BRIDGE.setToken(JSON.stringify(tokenData));
            devLog("[performLoginAfterRestore] 안드로이드 네이티브에 토큰 전달 완료");
          } catch (error) {
            devError("[performLoginAfterRestore] 안드로이드 토큰 전달 오류:", error);
          }
        }

        // 재인증 상태 초기화
        if (isReauthentication || isReauthenticationComplete) {
          devLog("[performLoginAfterRestore] 재인증 상태 초기화");
          setIsReauthentication(false);
          setIsReauthenticationComplete(false);
        }

        navigate(`/ws/${props.storeCode}/mobile/main`, { replace: true });
      } else {
        devError("[performLoginAfterRestore] 로그인 실패", { resultCode: signInResult.code });
        toast.error(t("로그인 중 오류가 발생했습니다"));
      }
    });
  };

  // ✅ "비밀번호 몰라요" 처리 핸들러 추가
  window.notifyPinVerificationFailedAndRestart = () => {
    devLog("[PIN 검증] 비밀번호 몰라요 - 재인증 프로세스 시작");

    // 0.3초 지연 후 재인증 프로세스 실행
    setTimeout(() => {
      // 재인증 상태로 설정
      setIsReauthentication(true);

      // 1단계(전화번호 입력)로 이동
      setCurrentStep(STEPS.PHONE_NUMBER);

      // PIN 검증 시도 횟수 초기화
      pinVerificationManager.resetAttempts();
      setPinVerificationAttempts(0);

      // 상태 초기화
      setPinVerificationState('idle');
      setIsLoading(false);

      devLog("[PIN 검증] 비밀번호 몰라요 처리 완료 - 전화번호 입력 단계로 이동");

      // 토스트 메시지 표시
      toast.info(t("PIN을 잊으셨군요. 다시 인증을 진행해주세요."), {
        autoClose: 1000,
        position: toast.POSITION.BOTTOM_CENTER
      });
    }, 300); // 0.3초 지연
  };

  return (
    <Container>
      <div className={styles.container} id="authFlowContainer">
        {renderProgressBar()}

        {/* 완료된 단계 헤더 */}
        <div className={styles.completedStepsContainer}>
          {renderCompletedStepHeaders()}
        </div>

        {/* 현재 단계 */}
        <div className={`${styles.stepContainer} ${currentStep > 0 ? styles.hasCompletedSteps : ''}`}>
          {renderCurrentStep()}
        </div>
      </div>
    </Container>
  );
}

export default AuthenticationFlow; 